import {
	faBoxesStacked,
	faContactBook,
	faFileSignature,
	faRoute,
	faShip,
} from '@fortawesome/pro-thin-svg-icons';
import { Values } from './objectEnums';

export const DATE = 'YYYY-MM-DD';
export const DATE_DMY = 'DD-MM-YYYY';
export const TIME = 'HH:mm';
export const DATE_AND_TIME = `${DATE} ${TIME}`;
export const DATE_AND_TIME_DMY = `${DATE_DMY} ${TIME}`;
export const DATE_SHORT_MONTH = 'DD MMM.';
export const SHORT_DATE_AND_TIME = `DD/MM/YY ${TIME}`;

export enum TcTermsParentTypes {
	Voyage = 'voyage',
	Fixture = 'fixture',
}

export enum TimeZoneFormat {
	UTC = 'UTC',
	LocalTime = 'Local Time',
}

export enum TimeZoneMode {
	Instant = 'instant',
	WallTime = 'wallTime',
}

export const NULL_STRING = '_null';

export const SUPPORT_EMAIL = 'support@clearvoyage.com';

export const SELECTED_ORGANIZATION_HEADER = 'CV-Selected-Organization';
export const SELECTED_ORGANIZATION_ID_LOCAL_STORAGE_KEY = 'CV-selected-organization';

export const IMPERSONATION_HEADER = 'CV-Impersonation';
export const IMPERSONATION_ID_LOCAL_STORAGE_KEY = 'CV-impersonation-key';

// Features that should only be available to a subset of customers, can be added below.
// It can then be checked with the `useFeatureFlag` hook on the frontend
// and the `hasFeatureFlag` function on backend.
// NOTE: Remove the feature flag and all references to it, when the feature is released to everyone
export const FeatureFlags = {
	PERFORMANCE: 'performance',
	ACCOUNTING: 'accounting',
	NAVIGARE_ACCOUNTING: 'navigare_accounting', // weird accounting practice only navigare needs
	ALLIANCE_ACCOUNTING: 'alliance_accounting',
	THE_SHIP: 'theShip',
	VESSELINDEX: 'vesselIndex',
	PAYMENTS_SYNC: 'payments_sync',
	ALBA: 'alba',
	DIS: 'dis',
	MERATUS: 'meratus',
} as const;

export const NavigareAccountingCodes = {
	5032: 'TC Other',
	5033: 'TC TM Rebillable',
	5309: 'Misc Expense',
	5393: 'TC TM Expenses',
	2635: 'TC Rebillable',
	5048: 'Other Income',
} as const;

export const AllianceAccountingCodes = {
	5100: 'Port Disbursement',
	5110: 'European Union Allowances - EUA',
	5115: 'FUEL-EU cost',
	5120: 'Security Guards',
	5150: 'Charterers Costs',
	5190: 'Other Voyage Related Expenses',
	5202: 'Other Bunker Expenses',
} as const;

export const MillisecondsPer = {
	SECOND: 1000,
	MINUTE: 60000,
	HOUR: 3600000,
	DAY: 86400000,
} as const;

export const MetersPer = {
	NAUTICAL_MILE: 1852,
	KILOMETER: 1000,
} as const;

export const VesselTypes = {
	DRY_BULK: 'DRY_BULK',
	CRUDE_PRODUCT: 'CRUDE_PRODUCT',
	CHEMICAL: 'CHEMICAL',
	LPG: 'LPG',
	LNG: 'LNG',
	CONTAINER: 'CONTAINER',
	WIND: 'WIND',
	FERRY: 'FERRY',
	ROPAX: 'ROPAX',
	MPP: 'MPP',
} as const;

export const VesselTypeLabels = {
	[VesselTypes.DRY_BULK]: 'Dry Bulk',
	[VesselTypes.CRUDE_PRODUCT]: 'Crude & Product Tanker',
	[VesselTypes.CHEMICAL]: 'Chemical Tanker',
	[VesselTypes.LPG]: 'LPG',
	[VesselTypes.LNG]: 'LNG',
	[VesselTypes.CONTAINER]: 'Container',
	[VesselTypes.WIND]: 'Wind',
	[VesselTypes.FERRY]: 'Ferry',
	[VesselTypes.ROPAX]: 'RoPax',
	[VesselTypes.MPP]: 'MPP',
} as const;

export const CANALS = [
	{ id: 2636, name: 'Bosporus' },
	{ id: 1013, name: 'Dardanelles Strait' },
	{ id: 4725, name: 'Kiel Canal' },
	{ id: 9002, name: 'Martin Garcia Canal' },
	{ id: 9001, name: 'Mitre Canal' },
	{ id: 4258, name: 'Panama Canal' },
	{ id: 7915, name: 'Suez Canal' },
	{ id: 1014, name: 'Sulina Channel' },
	{ id: 3599, name: 'Messina Strait' },
	{ id: 9006, name: 'Great Belt (Storebælt)' },
	{ id: 9007, name: 'Danish Sound (Øresund)' },
	{ id: 9008, name: 'Torres Strait' },

] as const;

export const DryBulkSizes = {
	Capesize: 'Capesize',
	PostPanamax: 'Post-Panamax',
	Kamsarmax: 'Kamsarmax',
	Panamax: 'Panamax',
	Ultramax: 'Ultramax',
	Supramax: 'Supramax',
	Handymax: 'Handymax',
	Handysize: 'Handysize',
	'mini-bulk': 'mini-bulk',
	Coasters: 'Coasters',
} as const;

export const CrudeProductSizes = {
	VLCC: 'VLCC',
	Suezmax: 'Suezmax',
	LR2: 'LR2',
	Aframax: 'Aframax',
	LR1: 'LR1',
	Panamax: 'Panamax',
};

export const ChemicalSizes = {
	Handymax: 'Handymax',
	Handysize: 'Handysize',
	'Intermediate (19K CBM)': 'Intermediate (19K CBM)',
	'Small Intermediate (12K-16K CBM)': 'Small Intermediate (12K-16K CBM)',
	'Small (8K CBM)': 'Small (8K CBM)',
	'Sub 5000 GT': 'Sub 5000 GT',
};

export const LPGSizes = {
	VLGC: 'VLGC',
	LGC: 'LGC',
	MGC: 'MGC',
	Handy: 'Handy',
	Coaster: 'Coaster',
};

export const LNGSizes = {
	QMax: 'Q-Max',
	QFlex: 'Q-Flex',
	Large: 'Large',
	Small: 'Small',
};

export const WINDSizes = {
	OSV: 'OSV',
	CSOV: 'CSOV',
	SOV: 'SOV',
};

export const ContainerSizes = {
	UltraLarge: 'Ultra Large',
	NewPanamax: 'New Panamax',
	PostPanamax: 'Post-Panamax',
	Panamax: 'Panamax',
	Feedermax: 'Feedermax',
	Feeder: 'Feeder',
	LCT: 'LCT',
};

export const FerrySizes = {
	Ropax: 'Ropax',
	RoRo: 'RoRo',
};

export const HIIFieldTypes = {
	NUMBER: 'NUMBER',
	CURRENCY: 'CURRENCY',
	TEXT: 'TEXT',
	DATE: 'DATE',
	PERCENTAGE: 'PERCENTAGE',
	SELECT: 'SELECT',
	EXCHANGE_RATE: 'EXCHANGE_RATE',
} as const;

export enum EngineType {
	MainEngine = 'Main Engine',
	AuxEngine = 'Aux Engine',
	Boiler = 'Boiler',
}

export enum EstimatorModeTypes {
	NORMAL = 'normal',
	MAP = 'map',
	GRID = 'grid',
}

export const LinerCostUnits = {
	LS: 'LS',
} as const;

export type LinerCostUnit =
	| Values<typeof CargoUnitTypes> |
	Values<typeof LinerCostUnits>;

export type LinerCostUnitType =
	| Values<typeof CargoUnitTypes> |
	Values<typeof LinerCostUnits>;
	
export enum LinerCostUnitDefaults {
	PER_CARGO_UNIT = 'PER_CARGO_UNIT',
	LUMPSUM = 'LUMPSUM',
}

export const LinerCostUnitDefaultLabels = {
	[LinerCostUnitDefaults.PER_CARGO_UNIT]: 'Per unit of cargo',
	[LinerCostUnitDefaults.LUMPSUM]: 'Lumpsum',
};

export enum ExportTypes {
	ACCRUALS = 'Accruals',
}

export enum ConsumptionReason {
	Propulsion = 'Propulsion',
	CargoHeating = 'Cargo Heating',
	BallastExchange = 'Ballast Exchange',
	Other = 'Other',
}

export const AlertTypes = {
	ETA_SLIPPAGE: 'ETA_SLIPPAGE',
	LATE_HIRE_INVOICING: 'LATE_HIRE_INVOICING',
	NEW_PAYMENT: 'NEW_PAYMENT',
	FLEET_PERFORMANCE: 'FLEET_PERFORMANCE',
	ARRIVAL_DEPARTURE: 'ARRIVAL_DEPARTURE',
	APPROVE_EXPENSE: 'APPROVE_EXPENSE',
	APPROVE_DA: 'APPROVE_DA',
	NEW_EXPENSE: 'NEW_EXPENSE',
	WEEKLY_SUMMARY: 'WEEKLY_SUMMARY',
} as const;

export const AlertTypeLabels = {
	[AlertTypes.ETA_SLIPPAGE]: 'Might miss laycan',
	[AlertTypes.LATE_HIRE_INVOICING]: 'New hire invoice is due',
	[AlertTypes.NEW_PAYMENT]: 'A new payment has been registrered',
};

export const PaymentTerms = {
	PERIOD_START: 'PERIOD_START',
	PERIOD_END: 'PERIOD_END',
	INVOICE_DATE: 'INVOICE_DATE',
};

export const PaymentTermLabels = {
	[PaymentTerms.PERIOD_START]: 'Start of each hire period',
	[PaymentTerms.PERIOD_END]: 'End of each hire period',
	[PaymentTerms.INVOICE_DATE]: 'Invoice date',
};

export enum CargoPortCallActivity {
	LOADING = 'Loading',
	DISCHARGING = 'Discharging',
}

export enum CargoLimitingFactor {
	VOLUME = 'VOLUME',
	DRAFT = 'DRAFT',
}

export enum FreightEstimatingMethod {
	CP_QUANTITY = 'CP_QUANTITY',
	MAX_INTAKE = 'MAX_INTAKE',
}

export enum VesselCapacityType {
	Grain = 'Grain',
	Bale = 'Bale',
}
export const CargoTermTypes = {
	FIO: 'FIO',
	FIOS: 'FIOS',
	LIFO: 'LIFO',
	FILO: 'FILO',
	LT: 'LT',
	FTFO: 'FTFO',
	FTLO: 'FTLO',
	EXFO: 'EXFO',
	EXLO: 'EXLO',
} as const;

export const CargoUnitTypes = {
	MT: 'MT',
	CBM: 'CBM',
	UNITS: 'UNITS',
	BARRELS: 'BARRELS',
	FRT: 'FRT',
	LT: 'LT',
	JAS: 'JAS',
} as const;

export const CargoContractStatusTypes = {
	Pending: 'Pending',
	TentativeBooking: 'Tentative booking',
	Contracted: 'Contracted',
	OnVoyage: 'On voyage',
	Cancelled: 'Cancelled',
} as const;

export type CargoContractStatus =
	typeof CargoContractStatusTypes[keyof typeof CargoContractStatusTypes];

export type ReletStatus = 'Yes' | 'No';

export const CargoDeclaredTypes = {
	Declared: 'Declared',
	Optional: 'Optional',
	Deleted: 'Deleted',
} as const;

export const CargoContractTypes = {
	SingleCargo: 'Single cargo',
} as const;

export const ConversionRates: Record<string, number> = {
	[CargoUnitTypes.CBM]: 1,
	[CargoUnitTypes.MT]: 35.3147,
	[CargoUnitTypes.UNITS]: 1,
	[CargoUnitTypes.BARRELS]: 6.28981,
	[CargoUnitTypes.FRT]: 35.3147,
	[CargoUnitTypes.LT]: 0.984,
} as const;

export const CargoUnitSingularLabels = {
	[CargoUnitTypes.MT]: 'MT',
	[CargoUnitTypes.CBM]: 'm³',
	[CargoUnitTypes.UNITS]: 'Unit',
	[CargoUnitTypes.BARRELS]: 'Barrel',
	[CargoUnitTypes.FRT]: 'FRT',
	[CargoUnitTypes.LT]: 'LT',
	[CargoUnitTypes.JAS]: 'JAS',
} as const;

export const CargoUnitLabels = {
	[CargoUnitTypes.MT]: 'MT',
	[CargoUnitTypes.CBM]: 'm³',
	[CargoUnitTypes.UNITS]: 'Units',
	[CargoUnitTypes.BARRELS]: 'Barrels',
	[CargoUnitTypes.FRT]: 'FRT',
	[CargoUnitTypes.LT]: 'LT',
	[CargoUnitTypes.JAS]: 'JAS',
} as const;

export const AnchorOrBerthActions = {
	LET_GO: 'Let go anchor',
	ANCHOR_AWEIGH: 'Anchor aweigh',
	FINISHED: 'Finished with engines',
} as const;

export const FuelTypes = {
	VLSFO: 'VLSFO', // VLS FO (≤ 0.5% sulfur)
	ULSFO: 'ULSFO', // ULS FO (≤ 0.1% sulfur)
	LSFO: 'LSFO', // LS FO (≤ 1.0% sulfur)
	HSFO: 'HSFO', // HS FO (≤ 3.5% sulfur)
	LSMGO: 'LSMGO', // Low Sulfur Marine Gas Oil
	ULSDO: 'ULSDO', // ULS DO/MGO (≤ ~0.001% sulfur)
	VLSDO: 'VLSDO', // VLS DO/MGO (≤ 0.1% sulfur)
	LSDO: 'LSDO', // LS DO/MGO (≤ 0.5% sulfur)
	HSDO: 'HSDO', // HS DO (> 0.5% sulfur)
	LNG: 'LNG', // LNG
	MGO: 'MGO',
	LNG_OTTO_MEDIUM_SPEED: 'LNG Otto - Medium Speed',
	LNG_OTTO_SLOW_SPEED: 'LNG Otto - Slow Speed',
	LNG_DIESEL_SLOW_SPEED: 'LNG Diesel - Slow Speed',
	LNG_LBSI: 'LNG - LBSI',
	LPG: 'LPG',
	H2: 'H2',
	NH3: 'NH3',
	METHANOL: 'Methanol',
	ETHANOL: 'Ethanol',
	BIO_DIESEL: 'Bio-diesel',
	HVO: 'HVO',
	BIO_LNG_OTTO_MEDIUM_SPEED: 'Bio-LNG Otto - Medium Speed',
	BIO_LNG_OTTO_SLOW_SPEED: 'Bio-LNG Otto - Slow Speed',
	BIO_LNG_OTTO_DIESEL_SLOW_SPEED: 'Bio-LNG Diesel - Slow Speed',
	BIO_LNG_LBSI: 'Bio-LNG - LBSI',
	BIO_METHANOL: 'Bio-methanol',
	OTHER: 'Other',
	BIO_H2: 'Bio-H2',
	E_DIESEL: 'e-diesel',
	E_METHANOL: 'e-methanol',
	E_LNG_OTTO_MEDIUM_SPEED: 'e-LNG Otto - Medium Speed',
	E_LNG_OTTO_SLOW_SPEED: 'e-LNG Otto - Slow Speed',
	E_LNG_OTTO_DIESEL_SLOW_SPEED: 'e-LNG Diesel - Slow Speed',
	E_LNG_LBSI: 'e-LNG - LBSI',
	E_H2: 'e-H2',
	E_NH3: 'e-NH3',
	E_LPG: 'e-LPG',
	E_DME: 'e-DME',
} as const;

export const TheShipFuelTypeMap = {
	'High-sulphur fuel oil (HSFO)': FuelTypes.HSFO,
	'Low sulfur marine gasoil (LSMGO)': FuelTypes.LSMGO,
	'Very-low sulphur fuel oil (VLSFO)': FuelTypes.VLSFO,
	'Liquefied Natural Gas': FuelTypes.LNG,
	'Marine Gas Oil (MGO)': FuelTypes.LSDO,
};

export const FuelSulfurContents = {
	[FuelTypes.VLSFO]: 0.5,
	[FuelTypes.ULSFO]: 0.1,
	[FuelTypes.LSFO]: 1,
	[FuelTypes.HSFO]: 3.5,
	[FuelTypes.LSMGO]: 0.1,
	[FuelTypes.ULSDO]: 0.001,
	[FuelTypes.VLSDO]: 0.1,
	[FuelTypes.LSDO]: 0.5,
	[FuelTypes.HSDO]: 0.6,
	[FuelTypes.LNG]: 0,
	[FuelTypes.MGO]: 0.1,
	[FuelTypes.LNG_OTTO_MEDIUM_SPEED]: 0,
	[FuelTypes.LNG_OTTO_SLOW_SPEED]: 0,
	[FuelTypes.LNG_DIESEL_SLOW_SPEED]: 0,
	[FuelTypes.LNG_LBSI]: 0,
	[FuelTypes.LPG]: 0,
	[FuelTypes.H2]: 0,
	[FuelTypes.NH3]: 0,
	[FuelTypes.METHANOL]: 0,
	[FuelTypes.ETHANOL]: 0,
	[FuelTypes.BIO_DIESEL]: 0.001,
	[FuelTypes.HVO]: 0,
	[FuelTypes.BIO_LNG_OTTO_MEDIUM_SPEED]: 0,
	[FuelTypes.BIO_LNG_OTTO_SLOW_SPEED]: 0,
	[FuelTypes.BIO_LNG_OTTO_DIESEL_SLOW_SPEED]: 0,
	[FuelTypes.BIO_LNG_LBSI]: 0,
	[FuelTypes.BIO_METHANOL]: 0,
	[FuelTypes.BIO_H2]: 0,
	[FuelTypes.E_DIESEL]: 0.001,
	[FuelTypes.E_METHANOL]: 0,
	[FuelTypes.E_LNG_OTTO_MEDIUM_SPEED]: 0,
	[FuelTypes.E_LNG_OTTO_SLOW_SPEED]: 0,
	[FuelTypes.E_LNG_OTTO_DIESEL_SLOW_SPEED]: 0,
	[FuelTypes.E_LNG_LBSI]: 0,
	[FuelTypes.E_H2]: 0,
	[FuelTypes.E_NH3]: 0,
	[FuelTypes.E_LPG]: 0,
	[FuelTypes.E_DME]: 0,
	[FuelTypes.OTHER]: 0,
};

export const FuelZoneTypes = {
	NORMAL: 'normal',
	ECA: 'eca',
} as const;

export const FuelZoneTypesLabels = {
	[FuelZoneTypes.NORMAL]: 'Propulsion (Normal)',
	[FuelZoneTypes.ECA]: 'Propulsion (ECA)',
} as const;

export const PortActivityTypes = {
	IDLE: 'idle',
	WORKING: 'working',
} as const;

export const PortActivityTypesLabels = {
	[PortActivityTypes.IDLE]: 'Idle',
	[PortActivityTypes.WORKING]: 'Working',
} as const;

export const FreightStates = {
	SUBMITTED: 'submitted',
	APPROVED: 'approved',
	POSTED: 'posted',
} as const;

export const FUEL_TYPE_EMISSIONS: Record<Values<typeof FuelTypes>, number> = {
	[FuelTypes.HSFO]: 3.114,
	[FuelTypes.LSFO]: 3.151,
	[FuelTypes.VLSFO]: 3.151,
	[FuelTypes.HSDO]: 3.206,
	[FuelTypes.LNG]: 2.750,
	[FuelTypes.ULSDO]: 3.206,
	[FuelTypes.ULSFO]: 3.151,
	[FuelTypes.LSDO]: 3.206,
	[FuelTypes.VLSDO]: 3.206,
	[FuelTypes.LSMGO]: 3.206,
	[FuelTypes.MGO]: 3.206,
	[FuelTypes.LNG_OTTO_MEDIUM_SPEED]: 2.750,
	[FuelTypes.LNG_OTTO_SLOW_SPEED]: 2.750,
	[FuelTypes.LNG_DIESEL_SLOW_SPEED]: 2.750,
	[FuelTypes.LNG_LBSI]: 2.750,
	[FuelTypes.LPG]: 3.000,
	[FuelTypes.H2]: 0,
	[FuelTypes.NH3]: 0,
	[FuelTypes.METHANOL]: 1.375,
	[FuelTypes.ETHANOL]: 1.913,
	[FuelTypes.BIO_DIESEL]: 2.834,
	[FuelTypes.HVO]: 3.115,
	[FuelTypes.BIO_LNG_OTTO_MEDIUM_SPEED]: 2.750,
	[FuelTypes.BIO_LNG_OTTO_SLOW_SPEED]: 2.750,
	[FuelTypes.BIO_LNG_OTTO_DIESEL_SLOW_SPEED]: 2.750,
	[FuelTypes.BIO_LNG_LBSI]: 2.750,
	[FuelTypes.BIO_METHANOL]: 1.375,
	[FuelTypes.BIO_H2]: 0,
	[FuelTypes.E_DIESEL]: 3.206,
	[FuelTypes.E_METHANOL]: 1.375,
	[FuelTypes.E_LNG_OTTO_MEDIUM_SPEED]: 2.750,
	[FuelTypes.E_LNG_OTTO_SLOW_SPEED]: 2.750,
	[FuelTypes.E_LNG_OTTO_DIESEL_SLOW_SPEED]: 2.750,
	[FuelTypes.E_LNG_LBSI]: 2.750,
	[FuelTypes.E_H2]: 0,
	[FuelTypes.E_NH3]: 0,
	[FuelTypes.E_LPG]: 3.000,
	[FuelTypes.E_DME]: 1.910,
	[FuelTypes.OTHER]: 3.115,
} as const;

export const VoyageBunkerTypes = {
	DELIVERY: 'delivery',
	REDELIVERY: 'redelivery',
	OWNERS_ACCOUNT: 'ownersAccount',
	CHARTERERS_ACCOUNT: 'charterersAccount',
} as const;

export const VoyageBunkerTypeLabels = {
	[VoyageBunkerTypes.DELIVERY]: 'delivery',
	[VoyageBunkerTypes.REDELIVERY]: 'redelivery',
	[VoyageBunkerTypes.OWNERS_ACCOUNT]: 'owner\'s account',
	[VoyageBunkerTypes.CHARTERERS_ACCOUNT]: 'charterer\'s account',
} as const;

export const SenderTypes = {
	SUPPLIER: 'supplier',
	CHARTERER: 'charterer',
	ORGANIZATION: 'organization',
	BROKER: 'broker',
} as const;

export const HIITypeLabels = {
	'hirePeriod': 'Hire Period',
	'addressCommission': 'Address Comm.',
	'brokerCommission': 'Broker Comm.',
	'expenseSubjectToHireDays': 'CVE',
	'vendorExpense': 'Vendor Expense',
	'voyageExpense': 'Voyage Expense',
	'voyageBunker': 'Voyage Bunker',
	'offHirePeriod': 'Off-Hire Period',
	'offHireAddressCommission': 'Off-Hire Address Comm.',
	'offHireBrokerCommission': 'Off-Hire Broker Comm.',
	'offHireExpenseSubjectToHireDays': 'Off-Hire CVE',
	'offHireBunker': 'Off-Hire Bunker',
	'grossBallastBonus': 'Gross Ballast Bonus',
	'ballastBonusAddressCommission': 'Address Comm.',
} as const;

export const BrokerCommissionItemTypes = [
	'brokerCommission',
	'bbItemBrokerCommission',
	'freightBrokerCommission',
	'offHireBrokerCommission',
	'ballastBonusBrokerCommission',
	'demurrageBrokerCommission',
	'despatchBrokerCommission',
];

export const TcInAllocationItemTypes = {
	VOYAGE_BUNKER: 'voyageBunker',
	VENDOR_EXPENSE: 'vendorExpense',
	VOYAGE_EXPENSE: 'voyageExpense',
	GROSS_BALLAST_BONUS: 'grossBallastBonus',
};

export const CalculationMethodForExpensesSubjectToHireDays = {
	ANNUALLY: 'annual',
	MONTHLY: 'monthly',
	FIXED: 'fixed',
} as const;

export const AccountTypes = {
	CHARTERER: 'charterer',
	OWNER: 'owner',
} as const;

export enum BunkerStemAccountTypes {
	CHARTERER = 'charterer',
	SELF = 'self',
	TC_IN_OWNER = 'tcInOwner'
};

export const BunkerStemAccountTypesLabels = {
	[BunkerStemAccountTypes.CHARTERER]: 'Charterer',
	[BunkerStemAccountTypes.SELF]: '',
	[BunkerStemAccountTypes.TC_IN_OWNER]: 'TC-In Owner'
};

export enum BunkerStemQuantityStatus {
	ESTIMATED = 'estimated',
	ACTUAL = 'actual',
}

export enum BunkerStemSuppliedAsPartOf {
	TC_OUT_DELIVERY = 'TC-Out Delivery',
	TC_OUT_REDELIVERY = 'TC-Out Redelivery',
	BB_OUT_DELIVERY = 'BB-Out Delivery',
	BB_OUT_REDELIVERY = 'BB-Out Redelivery',
	TC_IN_DELIVERY = 'TC-In Delivery',
}

export const AttachmentTypes = {
	VESSEL: 'vessel',
	FIXTURE: 'fixture',
	DOCUMENT: 'document',
	VOYAGE: 'voyage',
	HIRE_INVOICE: 'hireInvoice',
	FREIGHT_INVOICE: 'freightInvoice',
	BROKER_INVOICE: 'brokerInvoice',
	VOYAGE_EXPENSE: 'voyageExpense',
	VENDOR_EXPENSE: 'vendorExpense',
	VOYAGE_EXPENSE_RECEIVABLE: 'voyageExpenseReceivable',
	DISBURSEMENT_ACCOUNT: 'disbursementAccount',
	RobBunker: 'robBunker',
	ROB: 'rob',
	CONNECTION: 'connection',
	CARGO: 'cargo',
	BUNKER_STEM: 'bunkerStem',
	EUA: 'eua',
	OFF_HIRE_PERIOD: 'offHirePeriod',
	VOYAGE_OFF_HIRE_PERIOD: 'voyageOffHirePeriod',
	VOYAGE_BUNKER: 'voyageBunker',
} as const;

export enum CustomFieldType {
	NUMBER = 'NUMBER',
	TEXT = 'TEXT',
	DATEPICKER = 'DATEPICKER',
}

export enum CustomFieldParentTypes {
	BANK = 'bank',
	ORG = 'org',
}

export enum HireUnit {
	DAYS = 'days',
	MONTHS = 'months',
}

export const TYPE_TO_ICON = {
	[AttachmentTypes.VOYAGE]: faRoute,
	[AttachmentTypes.VESSEL]: faShip,
	[AttachmentTypes.FIXTURE]: faFileSignature,
	[AttachmentTypes.CONNECTION]: faContactBook,
	[AttachmentTypes.CARGO]: faBoxesStacked,
};

export const AttachmentTypeLabels = {
	[AttachmentTypes.VESSEL]: 'Vessel',
	[AttachmentTypes.FIXTURE]: 'Fixture',
	[AttachmentTypes.DOCUMENT]: 'Document',
	[AttachmentTypes.VOYAGE]: 'Voyage',
	[AttachmentTypes.HIRE_INVOICE]: 'Hire Invoice',
	[AttachmentTypes.BROKER_INVOICE]: 'Broker Invoice',
	[AttachmentTypes.VENDOR_EXPENSE]: 'Vendor Expense',
	[AttachmentTypes.VOYAGE_EXPENSE]: 'Voyage Expense',
	[AttachmentTypes.VOYAGE_EXPENSE_RECEIVABLE]: 'Receivable Voyage Expense',
	[AttachmentTypes.DISBURSEMENT_ACCOUNT]: 'Disbursement Account',
	[AttachmentTypes.FREIGHT_INVOICE]: 'Invoice',
	[AttachmentTypes.ROB]: 'ROB',
	[AttachmentTypes.RobBunker]: 'Bunker',
} as const;

export const ExpenseTypes = {
	PAYABLE: 'payable',
	BOTH: 'payable_and_receivable',
};

export const AMBIGUOUS_CARGO_CHARTERER_RECEIVABLE_MESSAGE = {
	title: 'Expense receivable created without charterer',
	description:
		'This voyage has multiple cargos with different charterers. Set the charterer on each receivable in the Expenses table.',
};

export enum DACostItemTypes {
	PORT_COST = 'Port cost',
	LINER_COST = 'Liner cost',
	TC_IN_OWNER_REBILLABLE = 'TC In owner rebillable',
	CHARTERER_COST = 'Charterer cost',
}

export const VoyageIdentifierTypes = {
	DEFAULT: 'default',
	CUSTOM: 'custom',
} as const;

export const HireTypes = {
	FIXED: 'fixed',
	FLOATING: 'floating',
} as const;

export const HireInvoiceItemStates = {
	PENDING: 'pending',
	INVOICED: 'invoiced',
	UNRESOLVED: 'unresolved',
	PAID: 'paid',
	PARTIAL: 'partial',
	OVERPAID: 'overpaid',
} as const;

export const PortCallParentTypes = {
	FIXTURE: 'fixture',
	VOYAGE: 'voyage',
} as const;

export const PerformanceEntryTypes = {
	FIXTURE: 'fixture',
	VESSEL: 'vessel',
	GENERIC_VESSEL: 'genericVessel',
};

export const DashboardTypes = {
	HOME: 'home',
	OFF_HIRE: 'offHire',
	FLEET_PNL: 'fleetPnl',
	TC_OUT: 'tcOut',
	TC_IN: 'tcIn',
	AGED_RECEIVABLES: 'agedeReceivables',
	AGED_PAYABLES: 'agedPayables',
	EUA_OUTSTANDINGS: 'euaOutstandings',
	OUTSTANDINGS: 'outstandings',
	PAYABLES: 'payables',
	OPEN_POSITIONS: 'openPositions',
} as const;

export const Departments = {
	MANAGEMENT: {
		key: 'management',
		name: 'Management',
	},
	CHARTERING: {
		key: 'chartering',
		name: 'Chartering',
	},
	OPERATIONS: {
		key: 'operations',
		name: 'Operations',
	},
	FINANCE: {
		key: 'finance',
		name: 'Finance',
	},
	ADMIN: {
		key: 'admin',
		name: 'Admin',
	},
} as const;

export type DepartmentKey = {
	[Key in keyof (typeof Departments)]: (typeof Departments)[Key]['key']
}[keyof typeof Departments];

export const FixtureCounterpartyTypes = {
	CHARTEREROWNER: 'charterer / owner',
	SUPPLIER: 'supplier',
	BROKER: 'broker',
} as const;

export const VesselOwnershipTypes = {
	OWNED: 'owned',
	TC_IN: 'tcIn',
	MARKET: 'market',
} as const;

export const VesselOwnershipTypeLabels = {
	[VesselOwnershipTypes.OWNED]: 'Owned',
	[VesselOwnershipTypes.TC_IN]: 'TC-IN',
	[VesselOwnershipTypes.MARKET]: 'Market',
} as const;

export const VoyageDocumentTags = [
	'Charter Party',
	'Recap',
	'Survey',
	'Voyage Expense',
] as const;

export const VesselDocumentTags = [
	'Cargo',
	'Certificate',
	'Classification',
	'Dimensions',
	'Engines & Propulsion',
	'Flag',
	'General',
	'H&M',
	'Loadline',
	'Mooring & Towage',
	'Ownership',
	'P&I',
	'PSC',
	'Questionnaire',
	'Tanks',
	'USCG',
] as const;

export const FixtureStates = {
	PENDING: 'pending',
	FIXED: 'fixed',
	COMMENCED: 'commenced',
	COMPLETED: 'completed',
} as const;

export const FixtureTypes = {
	TC_OUT: 'tcOut',
	TC_IN: 'tcIn',
	SPOT: 'spot',
	BB_OUT: 'bbOut',
} as const;

export const FixtureTypeLabels = {
	[FixtureTypes.BB_OUT]: 'BB-OUT',
	[FixtureTypes.TC_OUT]: 'TC-OUT',
	[FixtureTypes.TC_IN]: 'TC-IN',
	[FixtureTypes.SPOT]: 'VC-OUT',
} as const;

export enum BareboatItemType {
	CAPEX = 'Capex',
	OPEX = 'Opex',
	DRY_DOCK = 'Drydock',
	TC_HIRE = 'TC Hire',
	INTEREST = 'Interest',
}

// Values must be valid Moment units
export const PeriodUnits = {
	DAYS: 'days',
	MONTHS: 'months',
	YEARS: 'years',
} as const;

export const PeriodTypes = {
	OPTIONAL: 'optional',
	DECLARED: 'declared',
} as const;

export const DashboardPeriods = {
	YEAR: 'year',
	QUARTER: 'quarter',
	SIXTY_DAYS: 'sixtydays',
	HUNDRED_TWENTY_DAYS: 'hundredtwentydays',
} as const;

export type YearQuarterDashboardPeriods = Values<Pick<typeof DashboardPeriods, 'QUARTER' | 'YEAR'>>;
export type DaysDashboardPeriods = Values<Pick<typeof DashboardPeriods, 'SIXTY_DAYS' | 'HUNDRED_TWENTY_DAYS'>>;

export const ReceivableDaysByPeriod = {
	[DashboardPeriods.SIXTY_DAYS]: 60,
	[DashboardPeriods.HUNDRED_TWENTY_DAYS]: 120,
} as const;

export const ReceivableIntervalByPeriod = {
	[DashboardPeriods.SIXTY_DAYS]: 15,
	[DashboardPeriods.HUNDRED_TWENTY_DAYS]: 30,
} as const;

export const DashboardForwardPeriodLabels = {
	[DashboardPeriods.YEAR]: 'Rest of Year',
	[DashboardPeriods.QUARTER]: 'Rest of Quarter',
	[DashboardPeriods.SIXTY_DAYS]: '60 days',
	[DashboardPeriods.HUNDRED_TWENTY_DAYS]: '120 days',
} as const;

export const DashboardPeriodLabels = {
	[DashboardPeriods.YEAR]: 'Yearly',
	[DashboardPeriods.QUARTER]: 'Quarterly',
	[DashboardPeriods.SIXTY_DAYS]: '60 days',
	[DashboardPeriods.HUNDRED_TWENTY_DAYS]: '120 days',
} as const;

export const DashboardBackwardPeriodLabels = {
	[DashboardPeriods.YEAR]: 'YTD',
	[DashboardPeriods.QUARTER]: 'QTD',
} as const;

export const Colors = {
	PRIMARY: '#368ec9',
} as const;

export const ChartColors = {
	BLUE: '#096dd9',
	DARK_BLUE: '#06488f',
	LIGHTBLUE: '#5cc0ff',
	DARK_LIGHTBLUE: '#3d779c',
	CYAN: '#14CCAA',
	DARK_CYAN: '#0e806b',
	RED: '#DE6449',
	GREEN: '#43C59E',
	GREY: '#DADADA',
};

export const VesselConditionTypes = {
	BALLAST: 'ballast',
	LADEN: 'laden',
	LOADING: 'loading',
	DISCHARGING: 'discharging',
	IDLE: 'idle',
} as const;

export const ExpensesSubjectToHireDaysIntervals = {
	pcm: 'pcm',
	perDay: 'perDay',
};

export const ExpensesSubjectToHireDaysIntervalLabels = {
	[ExpensesSubjectToHireDaysIntervals.pcm]: 'Per calendar month',
	[ExpensesSubjectToHireDaysIntervals.perDay]: 'Per day',
};

export const UtcOffsets = [
	{
		name: 'UTC-12:00',
		offset: -720,
	},
	{
		name: 'UTC-11:00',
		offset: -660,
	},
	{
		name: 'UTC-10:00',
		offset: -600,
	},
	{
		name: 'UTC-09:30',
		offset: -570,
	},
	{
		name: 'UTC-09:00',
		offset: -540,
	},
	{
		name: 'UTC-08:00',
		offset: -480,
	},
	{
		name: 'UTC-07:00',
		offset: -420,
	},
	{
		name: 'UTC-06:00',
		offset: -360,
	},
	{
		name: 'UTC-05:00',
		offset: -300,
	},
	{
		name: 'UTC-04:00',
		offset: -240,
	},
	{
		name: 'UTC-03:30',
		offset: -210,
	},
	{
		name: 'UTC-03:00',
		offset: -180,
	},
	{
		name: 'UTC-02:00',
		offset: -120,
	},
	{
		name: 'UTC-01:00',
		offset: -60,
	},
	{
		name: 'UTC',
		offset: 0,
	},
	{
		name: 'UTC+01:00',
		offset: 60,
	},
	{
		name: 'UTC+02:00',
		offset: 120,
	},
	{
		name: 'UTC+03:00',
		offset: 180,
	},
	{
		name: 'UTC+03:30',
		offset: 210,
	},
	{
		name: 'UTC+04:00',
		offset: 240,
	},
	{
		name: 'UTC+04:30',
		offset: 270,
	},
	{
		name: 'UTC+05:00',
		offset: 300,
	},
	{
		name: 'UTC+05:30',
		offset: 330,
	},
	{
		name: 'UTC+05:45',
		offset: 345,
	},
	{
		name: 'UTC+06:00',
		offset: 360,
	},
	{
		name: 'UTC+06:30',
		offset: 390,
	},
	{
		name: 'UTC+07:00',
		offset: 420,
	},
	{
		name: 'UTC+08:00',
		offset: 480,
	},
	{
		name: 'UTC+08:45',
		offset: 525,
	},
	{
		name: 'UTC+09:00',
		offset: 540,
	},
	{
		name: 'UTC+09:30',
		offset: 570,
	},
	{
		name: 'UTC+10:00',
		offset: 600,
	},
	{
		name: 'UTC+10:30',
		offset: 630,
	},
	{
		name: 'UTC+11:00',
		offset: 660,
	},
	{
		name: 'UTC+12:00',
		offset: 720,
	},
	{
		name: 'UTC+12:45',
		offset: 765,
	},
	{
		name: 'UTC+13:00',
		offset: 780,
	},
	{
		name: 'UTC+14:00',
		offset: 840,
	},
];

export const SeedOrgName = 'ClearVoyage';
export const SeedUserName = 'Test User';

export const VesselCostType = {
	PerDay: 'PerDay',
	Lumpsum: 'Lumpsum',
};

export const Currencies = {
	AUD: 'AUD',
	BGN: 'BGN',
	BRL: 'BRL',
	CAD: 'CAD',
	CHF: 'CHF',
	CNY: 'CNY',
	CZK: 'CZK',
	DKK: 'DKK',
	EUA: 'EUA',
	EUR: 'EUR',
	GBP: 'GBP',
	HKD: 'HKD',
	HRK: 'HRK',
	HUF: 'HUF',
	IDR: 'IDR',
	INR: 'INR',
	ISK: 'ISK',
	ILS: 'ILS',
	JPY: 'JPY',
	KRW: 'KRW',
	MYR: 'MYR',
	MXN: 'MXN',
	NOK: 'NOK',
	NZD: 'NZD',
	PHP: 'PHP',
	PLN: 'PLN',
	RON: 'RON',
	RUB: 'RUB',
	SEK: 'SEK',
	SGD: 'SGD',
	THB: 'THB',
	TRY: 'TRY',
	USD: 'USD',
	ZAR: 'ZAR',
	AED: 'AED',
	MAD: 'MAD',
	DZD: 'DZD',
} as const;

export const CurrencySymbols = {
	[Currencies.USD]: '$',
	[Currencies.GBP]: '£',
	[Currencies.EUR]: '€',
	[Currencies.JPY]: '¥',
	[Currencies.RUB]: '₽',
	[Currencies.CNY]: '¥',
	[Currencies.INR]: '₹',
	[Currencies.THB]: '฿',
} as const;

export const BankAccountTypes = {
	TC_IN: 'TC_IN',
	OURS: 'OURS',
} as const;

export const PortActionTypes = {
	ANCHORING: 'anchoring',
	ARRIVAL: 'arrival',
	BALLAST_LEG: 'ballastLeg',
	BERTHING: 'berthing',
	BUNKER_STEM: 'bunkerStem',
	// BUNKERING - KEPT FOR A MIGRATION (20250210134303-pca-rob.ts)
	// DO NOT USE BUNKERING GOING FORWARD
	BUNKERING: 'bunkering',
	BUNKERS_ROB: 'bunkersRob',
	CANAL_TRANSIT: 'canalTransit',
	COMMENCEMENT: 'commencement',
	COMPLETION: 'completion',
	DELIVERY: 'delivering',
	DEPARTURE: 'departure',
	DISCHARGING: 'discharging',
	DRY_DOCKING: 'dryDocking',
	LOADING: 'loading',
	NOON_AT_SEA: 'nooAtSea',
	NOON_IN_PORT: 'noonInPort',
	PASSING: 'passing',
	READINESS: 'noticeOfReadiness',
	REDELIVERY: 'redelivering',
	REDELIVERING_TC_IN: 'redelivering_tc_in',
	REEVALUATION: 'reevaluation',
	REPOSITIONING: 'repositioning',
	STATEMENT_OF_FACTS: 'statementOfFacts',
	WAITING: 'waiting',
	EOSP: 'eosp',
	SOSP: 'sosp',
	ANCHORED: 'anchored',
	ANCHOR_AWEIGH: 'anchorAweigh',
} as const;

export const EstimatePortActionTypes = {
	BALLAST_LEG: 'ballastLeg',
	BUNKER_STEM: 'bunkerStem',
	CANAL_TRANSIT: 'canalTransit',
	COMMENCEMENT: 'commencement',
	COMPLETION: 'completion',
	DELIVERY: 'delivering',
	DISCHARGING: 'discharging',
	DRY_DOCKING: 'dryDocking',
	LOADING: 'loading',
	PASSING: 'passing',
	REDELIVERY: 'redelivering',
	REPOSITIONING: 'repositioning',
	WAITING: 'waiting',
} as const;

export const TsReportTypes = {
	10: 'EOSP',
	20: 'SOSP',
	30: 'Noon report',
	40: 'Arrival',
	50: 'Departure',
	60: 'SECA boundary crossing',
	70: 'Remaining on board (ROB)',
	80: 'Anchored',
	90: 'Weighed anchor',
	100: 'Log entry',
	200: 'Delivery',
	210: 'Redelivery',
	500: 'Tank initiation',
};

export const ShortPortActionTypes = {
	[PortActionTypes.DELIVERY]: 'DE',
	[PortActionTypes.REDELIVERY]: 'RD',
	[PortActionTypes.LOADING]: 'LO',
	[PortActionTypes.DISCHARGING]: 'DI',
	[PortActionTypes.BUNKER_STEM]: 'BU',
	[PortActionTypes.DRY_DOCKING]: 'DD',
	[PortActionTypes.CANAL_TRANSIT]: 'CT',
	[PortActionTypes.WAITING]: 'WA',
	[PortActionTypes.BALLAST_LEG]: 'CO',
	[PortActionTypes.PASSING]: 'PA',
	[PortActionTypes.REPOSITIONING]: 'RP',

} as const;

export const ShortEstimatePortActionTypes = {
	[EstimatePortActionTypes.DELIVERY]: 'DE',
	[EstimatePortActionTypes.REDELIVERY]: 'RD',
	[EstimatePortActionTypes.LOADING]: 'LO',
	[EstimatePortActionTypes.DISCHARGING]: 'DI',
	[EstimatePortActionTypes.BUNKER_STEM]: 'BU',
	[EstimatePortActionTypes.DRY_DOCKING]: 'DD',
	[EstimatePortActionTypes.CANAL_TRANSIT]: 'CT',
	[EstimatePortActionTypes.WAITING]: 'WA',
	[EstimatePortActionTypes.BALLAST_LEG]: 'CO',
	[EstimatePortActionTypes.PASSING]: 'PA',
	[EstimatePortActionTypes.REPOSITIONING]: 'RP',
	[EstimatePortActionTypes.COMMENCEMENT]: 'CO',
	[EstimatePortActionTypes.COMPLETION]: 'CP',
} as const;

export const getPortActionTypeLabel = (
	type: Values<typeof PortActionTypes>,
) => {
	if (type === PortActionTypes.BUNKERING) {
		return 'Bunkering';
	}

	return PortActionTypeLabels[type];
};

// Excluded list, but still needed for migration purposes ie. (20250210134303-pca-rob.ts)
export type DisplayablePortActionType = Exclude<
	Values<typeof PortActionTypes>,
	typeof PortActionTypes.BUNKERING
>;

export const PortActionTypeLabels: Record<DisplayablePortActionType, string> = {
	[PortActionTypes.DELIVERY]: 'TC-Out Delivery',
	[PortActionTypes.REDELIVERY]: 'TC-Out Redelivery',
	[PortActionTypes.LOADING]: 'Loading',
	[PortActionTypes.DISCHARGING]: 'Discharging',
	[PortActionTypes.DRY_DOCKING]: 'Dry Docking',
	[PortActionTypes.CANAL_TRANSIT]: 'Canal Transit',
	[PortActionTypes.WAITING]: 'Waiting',
	[PortActionTypes.BALLAST_LEG]: 'Ballast Leg',
	[PortActionTypes.COMMENCEMENT]: 'Commencement',
	[PortActionTypes.COMPLETION]: 'Completion',
	[PortActionTypes.BUNKER_STEM]: 'Bunker Stem',
	[PortActionTypes.PASSING]: 'Passing',
	[PortActionTypes.REPOSITIONING]: 'Repositioning',
	[PortActionTypes.REDELIVERING_TC_IN]: 'TC-In Redelivery',
	[PortActionTypes.NOON_AT_SEA]: 'Noon At Sea',
	[PortActionTypes.NOON_IN_PORT]: 'Noon In Port',
	[PortActionTypes.DEPARTURE]: 'Departure',
	[PortActionTypes.ARRIVAL]: 'Arrival',
	[PortActionTypes.BUNKERS_ROB]: 'Bunkers ROB',
	[PortActionTypes.READINESS]: 'Notice of Readiness',
	[PortActionTypes.ANCHORING]: 'Anchoring',
	[PortActionTypes.BERTHING]: 'Berthing',
	[PortActionTypes.STATEMENT_OF_FACTS]: 'Statement of Facts',
	[PortActionTypes.REEVALUATION]: 'Reevaluation',
	[PortActionTypes.EOSP]: 'EOSP',
	[PortActionTypes.SOSP]: 'SOSP',
	[PortActionTypes.ANCHORED]: 'Anchored',
	[PortActionTypes.ANCHOR_AWEIGH]: 'Weighed Anchor',
} as const;

export const EstimatePortActionTypeLabels: Record<Values<typeof EstimatePortActionTypes>, string> = {
	[EstimatePortActionTypes.DELIVERY]: 'TC-Out Delivery',
	[EstimatePortActionTypes.REDELIVERY]: 'TC-Out Redelivery',
	[EstimatePortActionTypes.LOADING]: 'Loading',
	[EstimatePortActionTypes.DISCHARGING]: 'Discharging',
	[EstimatePortActionTypes.DRY_DOCKING]: 'Dry Docking',
	[EstimatePortActionTypes.CANAL_TRANSIT]: 'Canal Transit',
	[EstimatePortActionTypes.WAITING]: 'Waiting',
	[EstimatePortActionTypes.BALLAST_LEG]: 'Commencement',
	[EstimatePortActionTypes.COMMENCEMENT]: 'Commencement',
	[EstimatePortActionTypes.COMPLETION]: 'Completion',
	[EstimatePortActionTypes.BUNKER_STEM]: 'Bunker Stem',
	[EstimatePortActionTypes.PASSING]: 'Passing',
	[EstimatePortActionTypes.REPOSITIONING]: 'Repositioning',
} as const;

export const PnlGroups = {
	ADDRESS_COMMISSIONS: 'ADDRESS_COMMISSIONS',
	BROKER_COMMISSIONS: 'BROKER_COMMISSIONS',
	BALLAST_BONUSES: 'BALLAST_BONUSES',
	OWNERS_BUNKERS: 'OWNERS_BUNKERS',
	CHARTERERS_BUNKERS: 'CHARTERERS_BUNKERS',
	EXPENSES_SUBJECT_TO_HIRE_DAYS: 'EXPENSES_SUBJECT_TO_HIRE_DAYS',
	REVENUE_SUBJECT_TO_DAYS: 'REVENUES_SUBJECT_TO_DAYS',
	HIRE: 'HIRE',
	OFFHIRE: 'OFFHIRE',
	OWNER_EXPENSES: 'OWNER_EXPENSES',
	CHARTERER_EXPENSES: 'CHARTERER_EXPENSES',
	DEMURRAGE_DESPATCH: 'DEMURRAGE_DESPATCH',
	FREIGHT: 'FREIGHT',
	OTHER_REVENUE: 'OTHER_REVENUE',
	BAREBOAT_ITEM: 'BAREBOAT_ITEM',
	EUAS: 'EUAS',
} as const;

export enum PnlTypes {
	ACTUAL = 'actual',
	ESTIMATED = 'estimated',
	INVOICED = 'invoiced',
}

export const UnnamedBankAccountName = 'Unnamed Bank Account';
export const BankAccountName = 'Bank Account Name';
export const ShowNothingInvoiceCompanyName = 'Show nothing';

export const QuantityOptionTypes = {
	MOLOO: 'MOLOO',
	MOLCHOPT: 'MOLCHOPT',
};

export enum EstimateStatus {
	'Available',
	'Working',
	'Subjects',
	'Fixed',
	'Pending',
}

export const EstimateStatusLabels = {
	[EstimateStatus.Pending]: 'Pending',
	[EstimateStatus.Fixed]: 'Fixed',
};

export const FreightRateType = {
	PER_UNIT: 'PER_UNIT',
	LUMPSUM: 'LUMPSUM',
} as const;

export const TsReportToPortActionMap: Partial<
	Record<keyof typeof TsReportTypes, (typeof PortActionTypes)[keyof typeof PortActionTypes]>
> = {
	10: PortActionTypes.EOSP,
	20: PortActionTypes.SOSP,
	30: PortActionTypes.NOON_AT_SEA,
	40: PortActionTypes.ARRIVAL,
	50: PortActionTypes.DEPARTURE,
	80: PortActionTypes.ANCHORING,
	90: PortActionTypes.ANCHOR_AWEIGH,
	200: PortActionTypes.DELIVERY,
	210: PortActionTypes.REDELIVERY,
	500: PortActionTypes.BUNKER_STEM,
};

export const DefaultCargoUnit = CargoUnitTypes.MT;

export const InvoiceLogoModes = {
	NONE: 'NONE',
	ORGANIZATION_LOGO: 'ORGANIZATION_LOGO',
	FIXING_COMPANY_LOGO: 'FIXING_COMPANY_LOGO',
	CUSTOM_LOGO: 'CUSTOM_LOGO',
} as const;

export const CardinalDirections = {
	N: 0,
	NNE: 22.5,
	NE: 45,
	ENE: 67.5,
	E: 90,
	ESE: 112.5,
	SE: 135,
	SSE: 157.5,
	S: 180,
	SSW: 202.5,
	SW: 225,
	WSW: 247.5,
	W: 270,
	WNW: 292.5,
	NW: 315,
	NNW: 337.5,
} as const;

export const UNKNOWN_PORT = 'Unknown Port';

export const NoonReportThresholds = {
	AVERAGE_SPEED: {
		min: 0, max: 30,
	},
	OBSERVED_DISTANCE: {
		min: 0, max: 750,
	},
	ENGINE_DISTANCE: {
		min: 0, max: 750,
	},
	FORWARD_DRAFT: {
		min: 0, max: 25,
	},
	MID_DRAFT: {
		min: 0, max: 25,
	},
	AFT_DRAFT: {
		min: 0, max: 25,
	},
	SLIP: {
		min: -100, max: 100,
	},
	MAIN_ENGINE_OUTPUT: {
		min: 0, max: 10000,
	},
	AVERAGE_RPM: {
		min: 0, max: 400,
	},
	STEAMING_HOURS: {
		min: 0, max: 24,
	},
	WIND_FORCE: {
		min: 0, max: 12,
	},
	CURRENT_SPEED: {
		min: 0, max: 50,
	},
	SEA_HEIGHT: {
		min: 0, max: 25,
	},
	SWELL_HEIGHT: {
		min: 0, max: 25,
	},
	CONSUMPTION: {
		min: 0, max: 75,
	},
};

export const ChildBunkerTypes = {
	VESSEL: 'vessel',
	VOYAGE: 'voyage',
} as const;

export enum VcPortTypes {
	COMPLETION = 'Completion',
	COMMENCEMENT = 'Commencement',
}

export enum PnlItemTypes {
	FREIGHT = 'Freight',
	DEMURRAGE_DESPATCH = 'Demurrage & Despatch',
	PORT_COST = 'Port Cost',
	GROSS_HIRE = 'Gross Hire',
	GROSS_BALLAST_BONUS = 'Gross Ballast Bonus',
	ADDRESS_COMMISSIONS = 'Address Commissions',
	BROKER_COMMISSIONS = 'Broker Commissions',
	BUNKER_EXPENSES = 'Bunker Expenses',
	EXPENSES_SUBJECT_TO_HIRE_DAYS = 'Expenses Subject to Hire Days',
	REVENUE_SUBJECT_TO_DAYS = 'Revenue Subject to Days',
	REIMBURSABLE_EXPENSES = 'Charterer\'s Expenses',
	BUNKER_LOSS_GAIN = 'Bunker Gain/(Loss)',
	REPOSITIONING_BUNKERS = 'Repositioning Bunkers',
	OTHER_REVENUE = 'Charterer\'s Expenses',
	TOTAL_REVENUE = 'Total Revenue',
	PAYABLE_EXPENSES = 'Owner\'s Expenses',
	OFF_HIRE = 'Off Hire',
	CAPEX = 'CAPEX',
	OPEX = 'OPEX',
	TC_IN_COSTS = 'TC-In Costs',
	OTHER_EXPENSES = 'Owner\'s Expenses',
	OTHER_VOYAGE_EXPENSES = 'Other Voyage Expenses',
	TOTAL_EXPENSES = 'Total Expenses',
	FINAL_VOYAGE_PROFIT = 'Voyage Profit',
	FINAL_VOYAGE_PROFIT_PER_DAY = 'TCE',
	DAYS = 'Total Days',
	NET_RESULT = 'Net Result',
	NET_RESULT_PER_DAY = 'Net Result Per Day',
}

export enum PnlRenderTypes {
	CURRENCY = 'currency',
	PERCENTAGE = 'percentage',
}

export enum LaytimeCalculationMethods {
	SHINC = 'SHINC (7 days/week)',
	SAT_MON_SHEX = 'Sat12-Mon08SHEX (5.17 days/week)',
	SUN_SHEX = 'Sun00-Sun24SHEX (6 days/week)',
	FRI_MON_SHEX = 'Fri17-Mon08SHEX (4.38 days/week)',
	SAT_PM_MON_SHEX = 'Sat17-Mon08SHEX (5.38 days/week)',
	SAT_AM_MON_SHEX = 'Sat12-Mon00SHEX (5.5 days/week)',
	CQD = 'CQD (7 days/week)',
}

export const shortLaytimeCalculationMethods: Record<LaytimeCalculationMethods, string> = {
	[LaytimeCalculationMethods.SHINC]: 'SHINC (7)',
	[LaytimeCalculationMethods.SAT_MON_SHEX]: 'Sat12-Mon08SHEX (5.17)',
	[LaytimeCalculationMethods.SUN_SHEX]: 'Sun00-Sun24SHEX (6)',
	[LaytimeCalculationMethods.FRI_MON_SHEX]: 'Fri17-Mon08SHEX (4.38)',
	[LaytimeCalculationMethods.SAT_PM_MON_SHEX]: 'Sat17-Mon08SHEX (5.38)',
	[LaytimeCalculationMethods.SAT_AM_MON_SHEX]: 'Sat12-Mon00SHEX (5.5)',
	[LaytimeCalculationMethods.CQD]: 'CQD (7)',
};

export const LaytimeDaysPerWeek = {
	[LaytimeCalculationMethods.SHINC]: 7,
	[LaytimeCalculationMethods.SAT_MON_SHEX]: 5.166666667,
	[LaytimeCalculationMethods.SUN_SHEX]: 6,
	[LaytimeCalculationMethods.FRI_MON_SHEX]: 4.375,
	[LaytimeCalculationMethods.SAT_PM_MON_SHEX]: 5.375,
	[LaytimeCalculationMethods.SAT_AM_MON_SHEX]: 5.5,
	[LaytimeCalculationMethods.CQD]: 7,
};

export const DEFAULT_SEA_ROUTE_SETTINGS = {
	allowSuezTransit: true,
	allowPanamaTransit: true,
	allowKielTransit: true,
	allowMartinGarciaTransit: true,
	allowMitreTransit: true,
	allowGreatBeltTransit: true,
	allowDanishSoundTransit: true,
	allowTorresStraitTransit: true,
	avoidHRA: false,
	avoidSeca: false,
	allowIceAreas: false,
	speed: 12,
	seaMargin: 0,
	overrideDraft: undefined,
};

export const SEA_PASSAGE_TOGGLES = [
	{ key: 'allowKielTransit', label: 'Allow Kiel Canal' },
	{ key: 'allowSuezTransit', label: 'Allow Suez Canal' },
	{ key: 'allowPanamaTransit', label: 'Allow Panama Canal' },
	{ key: 'allowMartinGarciaTransit', label: 'Allow Martin Garcia Canal' },
	{ key: 'allowMitreTransit', label: 'Allow Mitre Canal' },
	{ key: 'allowGreatBeltTransit', label: 'Allow Great Belt (Storebælt)' },
	{ key: 'allowDanishSoundTransit', label: 'Allow Danish Sound (Øresund)' },
	{ key: 'allowTorresStraitTransit', label: 'Allow Torres Strait' },
	{ key: 'avoidHRA', label: 'Avoid High Risk Areas (HRA)' },
	{ key: 'avoidSeca', label: 'Avoid ECA' },
	{ key: 'allowIceAreas', label: 'Allow Ice Areas' },
] as const;

export type SeaPassageToggleKey = typeof SEA_PASSAGE_TOGGLES[number]['key'];

export type SeaPassageToggles = Record<SeaPassageToggleKey, boolean>;

export const pickSeaPassageToggles = (
	settings: Partial<Record<SeaPassageToggleKey, unknown>> | null | undefined,
): Partial<SeaPassageToggles> => SEA_PASSAGE_TOGGLES.reduce((acc, { key }) => (
	typeof settings?.[key] === 'boolean' ? { ...acc, [key]: settings[key] } : acc
), {});

export enum PortCallStatus {
	INTENDED = 'intended',
	FIRM = 'firm',
	FOR_INSTRUCTIONS = 'for instructions',
}

export enum QuantityOptions {
	MOLOO = 'MOLOO',
	MOLCHOPT = 'MOLCHOPT',
}

export enum LaytimeTerms {
	REVERSIBLE = 'Reversible',
	NON_REVERSIBLE = 'Non-reversible',
	AVERAGED = 'Averaged',
}

export enum LaytimeIntervals {
	DAY = 'day',
	HOUR = 'hour',
}

export enum DemurrageTerms {
	DEMURRAGE = 'Demurrage',
	DETENTION = 'Detention',
}

export enum LaytimeSettlementTypes {
	DEMURRAGE = 'Demurrage',
	DESPATCH = 'Despatch',
}

export enum SofActions {
	ALL_FAST = 'All fast',
	ANCHORED = 'Anchored',
	ARRIVED_PILOT_STATION = 'Arrived pilot station',
	BERTHED = 'Berthed',
	CARGO_DOCUMENTS_ON_BOARD = 'Cargo documents onboard',
	COMMENCED_LOADING = 'Commenced loading',
	COMMENCED_DISCHARGING = 'Commenced discharging',
	COMPLETED_LOADING = 'Completed loading',
	COMPLETED_DISCHARGING = 'Completed discharging',
	DEPARTED_PILOT_STATION = 'Departed pilot station',
	FIRST_LINE = 'First line',
	FREE_PRATIQUE_GRANTED = 'Free pratique granted',
	NOTICE_OF_READINESS = 'Notice of Readiness',
	PILOT_ON_BOARD = 'Pilot on board',
	PILOT_DISEMBARKED = 'Pilot disembarked',
	UNBERTHED = 'Unberthed',
}

export enum AccountingItemTypes {
	PAYABLE = 'Payable',
	RECEIVABLE = 'Receivable',
}

export enum AccountingItems {
	TcOutInvoices = 'Invoices - TC Out',
	TcInInvoices = 'Invoices - TC In',
	VCOutInvoices = 'Invoices - VC Out',
	VendorExpenses = 'Vendor expenses',
	BrokerInvoices = 'Broker Invoices',
	DisbursementAccounts = 'Disbursement Accounts',
}

export enum AccountingItemApprovalStates {
	PENDING = 'pending',
	SUBMITTED = 'submitted',
	APPROVED = 'approved',
	REJECTED = 'rejected',
	PAID = 'paid',
	PARTIAL = 'partial',
	OVERPAID = 'overpaid',
	OVERDUE = 'overDue',
}

export const ExpenseActions = {
	SUBMITTED: 'submitted',
	UNSUBMITTED: 'unsubmitted',
	APPROVED: 'approved',
	REJECTED: 'rejected',
	MARKED_PAID: 'markedPaid',
	MARKED_PARTIAL_PAID: 'markedPartialPaid',
	MARKED_OVERPAID: 'markedOverpaid',
	MARKED_UNPAID: 'markedUnpaid',
} as const;

export enum EstimatedItemTypes {
	REVENUE = 'revenue',
	EXPENSE = 'expense',
}

export const AccountingSystemProviders = {
	CODAT: 'codat',
	NAVISION: 'navision',
	INTACCT: 'intacct',
	XERO: 'xero',
	TWINFIELDS: 'twinfields',
	'365fo': '365fo',
	'365bc': '365bc',
} as const;

export const AccountingSystemStrategies = {
	GLOBAL: 'global',
	VESSEL: 'vessel',
	VOYAGE: 'voyage',
} as const;

export enum InvoiceTemplates {
	CLASSIC = 'classic',
	NEO = 'neo',
}

export enum CapexOpexInput {
	Yearly = 'Yearly',
	Monthly = 'Monthly',
}

export enum ContractNamingConventions {
	LEGACY = 'legacy',
	DEFAULT = 'default',
	FREE = 'free text',
}

export const DeferredAccrualFields = {
	DEFERRED_TC_HIRE: 'Deferred TC Hire',
	DEFERRED_INTEREST: 'Deferred Interest',
	DEFERRED_ADDCOM: 'Deferred Address Commission',
	DEFERRED_BROKERS_COM: 'Deferred Broker\'s Commission (Paid By Charterer)',
	DEFERRED_BROKERS_INVOICE: 'Deferred Broker\'s Invoice',
	DEFERRED_VENDOR_EXPENSE: 'Deferred Vendor Expense',
	DEFERRED_DISBURSEMENT_ACCOUNT: 'Deferred Disbursement Account',
	DEFERRED_CHARTERERS_EXPENSE: 'Deferred Charterer\'s Expense',
	DEFERRED_CHARTERERS_EXPENSE_SUBJECT: 'Deferred Charterer\'s Expense Subject To Days',
	DEFERRED_OWNERS_EXPENSE: 'Deferred Owner\'s Expense',
	DEFERRED_OFF_HIRE_TIME: 'Deferred Off-Hire Time',
	DEFERRED_OFF_HIRE_BUNKER: 'Deferred Off-Hire Bunker',
	DEFERRED_OFF_HIRE_ADDCOM: 'Deferred Off-Hire Address Commission',
	DEFERRED_OFF_HIRE_BROKERS_COM: 'Deferred Off-Hire Broker\'s Commission (Paid By Charterer)',
	DEFERRED_OFF_HIRE_CHARTERERS_EXPENSE_SUBJECT: 'Deferred Off-Hire Charterer\'s Expense Subject To Days',
	DEFERRED_OTHER_REVENUE: 'Deferred Other Revenue',
	DEFERRED_BUNKER_SALE: 'Deferred Bunker Sale',
	DEFERRED_BUNKER_PURCHASE: 'Deferred Bunker Purchase',
	DEFERRED_BUNKER_ADJUSTMENT: 'Deferred Bunker Adjustment',
	DEFERRED_BUNKER_CONSUMPTION: 'Deferred Bunker Consumption',
	DEFERRED_TC_HIRE_COST: 'Deferred TC Hire Cost',
	DEFERRED_FREIGHT_REVENUE: 'Deferred Freight Revenue',
	DEFERRED_DEMURRAGE_REVENUE: 'Deferred Demurrage Revenue',
	DEFERRED_DESPATCH_REVENUE: 'Deferred Despatch Revenue',
	DEFERRED_OVERAGE_REVENUE: 'Deferred Overage Revenue',
	DEFERRED_DEADFREIGHT_REVENUE: 'Deferred Deadfreight Revenue',
	DEFERRED_REVENUE_SUBJECT: 'Deferred Revenue Subject To Days',
	DEFERRED_CAPEX: 'Deferred Capex',
	DEFERRED_OPEX: 'Deferred Opex',
	DEFERRED_DRYDOCK: 'Deferred Drydock',
	DEFERRED_BB_TC_HIRE: 'Deferred TC Hire (BB)',
} as const;

export const ActualAccrualFields = {
	ACTUAL_TC_HIRE: 'Actual TC Hire',
	ACTUAL_ADDCOM: 'Actual Address Commission',
	ACTUAL_BROKERS_COM: 'Actual Broker\'s Commission (Paid By Charterer)',
	ACTUAL_BROKERS_INVOICE: 'Actual Broker\'s Invoice',
	ACTUAL_VENDOR_EXPENSE: 'Actual Vendor Expense',
	ACTUAL_DISBURSEMENT_ACCOUNT: 'Actual Disbursement Account',
	ACTUAL_CHARTERERS_EXPENSE: 'Actual Charterer\'s Expense',
	ACTUAL_CHARTERERS_EXPENSE_SUBJECT: 'Actual Charterer\'s Expense Subject To Days',
	ACTUAL_OWNERS_EXPENSE: 'Actual Owner\'s Expense',
	ACTUAL_OFF_HIRE_TIME: 'Actual Off-Hire Time',
	ACTUAL_OFF_HIRE_BUNKER: 'Actual Off-Hire Bunker',
	ACTUAL_OFF_HIRE_ADDCOM: 'Actual Off-Hire Address Commission',
	ACTUAL_OFF_HIRE_BROKERS_COM: 'Actual Off-Hire Broker\'s Commission (Paid By Charterer)',
	ACTUAL_OFF_HIRE_CHARTERERS_EXPENSE_SUBJECT: 'Actual Off-Hire Charterer\'s Expense Subject To Days',
	ACTUAL_OTHER_REVENUE: 'Actual Other Revenue',
	ACTUAL_BUNKER_SALE: 'Actual Bunker Sale',
	ACTUAL_BUNKER_PURCHASE: 'Actual Bunker Purchase',
	ACTUAL_BUNKER_ADJUSTMENT: 'Actual Bunker Adjustment',
	ACTUAL_BUNKER_CONSUMPTION: 'Actual Bunker Consumption',
	ACTUAL_TC_HIRE_COST: 'Actual TC Hire Cost',
	ACTUAL_FREIGHT_REVENUE: 'Actual Freight Revenue',
	ACTUAL_DEMURRAGE_REVENUE: 'Actual Demurrage Revenue',
	ACTUAL_DESPATCH_REVENUE: 'Actual Despatch Revenue',
	ACTUAL_OVERAGE_REVENUE: 'Actual Overage Revenue',
	ACTUAL_DEADFREIGHT_REVENUE: 'Actual Deadfreight Revenue',
	ACTUAL_REVENUE_SUBJECT: 'Actual Revenue Subject To Days',
	ACTUAL_CAPEX: 'Actual Capex',
	ACTUAL_OPEX: 'Actual Opex',
	ACTUAL_DRYDOCK: 'Actual Drydock',
	ACTUAL_BB_TC_HIRE: 'Actual TC Hire (BB)',
	ACTUAL_INTEREST: 'Actual Interest',
} as const;

export const AccrualFieldMap: Record<keyof typeof DeferredAccrualFields, keyof typeof ActualAccrualFields> = {
	DEFERRED_TC_HIRE: 'ACTUAL_TC_HIRE',
	DEFERRED_ADDCOM: 'ACTUAL_ADDCOM',
	DEFERRED_BROKERS_COM: 'ACTUAL_BROKERS_COM',
	DEFERRED_BROKERS_INVOICE: 'ACTUAL_BROKERS_INVOICE',
	DEFERRED_VENDOR_EXPENSE: 'ACTUAL_VENDOR_EXPENSE',
	DEFERRED_DISBURSEMENT_ACCOUNT: 'ACTUAL_DISBURSEMENT_ACCOUNT',
	DEFERRED_CHARTERERS_EXPENSE: 'ACTUAL_CHARTERERS_EXPENSE',
	DEFERRED_CHARTERERS_EXPENSE_SUBJECT: 'ACTUAL_CHARTERERS_EXPENSE_SUBJECT',
	DEFERRED_OWNERS_EXPENSE: 'ACTUAL_OWNERS_EXPENSE',
	DEFERRED_OFF_HIRE_TIME: 'ACTUAL_OFF_HIRE_TIME',
	DEFERRED_OFF_HIRE_BUNKER: 'ACTUAL_OFF_HIRE_BUNKER',
	DEFERRED_OFF_HIRE_ADDCOM: 'ACTUAL_OFF_HIRE_ADDCOM',
	DEFERRED_OFF_HIRE_BROKERS_COM: 'ACTUAL_OFF_HIRE_BROKERS_COM',
	DEFERRED_OFF_HIRE_CHARTERERS_EXPENSE_SUBJECT: 'ACTUAL_OFF_HIRE_CHARTERERS_EXPENSE_SUBJECT',
	DEFERRED_OTHER_REVENUE: 'ACTUAL_OTHER_REVENUE',
	DEFERRED_BUNKER_SALE: 'ACTUAL_BUNKER_SALE',
	DEFERRED_BUNKER_PURCHASE: 'ACTUAL_BUNKER_PURCHASE',
	DEFERRED_BUNKER_ADJUSTMENT: 'ACTUAL_BUNKER_ADJUSTMENT',
	DEFERRED_BUNKER_CONSUMPTION: 'ACTUAL_BUNKER_CONSUMPTION',
	DEFERRED_TC_HIRE_COST: 'ACTUAL_TC_HIRE_COST',
	DEFERRED_FREIGHT_REVENUE: 'ACTUAL_FREIGHT_REVENUE',
	DEFERRED_DEMURRAGE_REVENUE: 'ACTUAL_DEMURRAGE_REVENUE',
	DEFERRED_DESPATCH_REVENUE: 'ACTUAL_DESPATCH_REVENUE',
	DEFERRED_OVERAGE_REVENUE: 'ACTUAL_OVERAGE_REVENUE',
	DEFERRED_DEADFREIGHT_REVENUE: 'ACTUAL_DEADFREIGHT_REVENUE',
	DEFERRED_REVENUE_SUBJECT: 'ACTUAL_REVENUE_SUBJECT',
	DEFERRED_CAPEX: 'ACTUAL_CAPEX',
	DEFERRED_OPEX: 'ACTUAL_OPEX',
	DEFERRED_DRYDOCK: 'ACTUAL_DRYDOCK',
	DEFERRED_BB_TC_HIRE: 'ACTUAL_BB_TC_HIRE',
	DEFERRED_INTEREST: 'ACTUAL_INTEREST',
};

export const AccrualsWithCommencementFields = {
	[ActualAccrualFields.ACTUAL_FREIGHT_REVENUE]: true,
	[ActualAccrualFields.ACTUAL_DEMURRAGE_REVENUE]: true,
	[ActualAccrualFields.ACTUAL_DESPATCH_REVENUE]: true,
	[ActualAccrualFields.ACTUAL_OVERAGE_REVENUE]: true,
	[ActualAccrualFields.ACTUAL_DEADFREIGHT_REVENUE]: true,
	[ActualAccrualFields.ACTUAL_OTHER_REVENUE]: true,
	[ActualAccrualFields.ACTUAL_BROKERS_COM]: true,
	[ActualAccrualFields.ACTUAL_ADDCOM]: true,
};

export enum AccrualItemTypes {
	ENTRY = 'entry',
	REVERSAL = 'reversal',
}

export enum AccrualItemStates {
	OPEN = 'open',
	CLOSED = 'closed',
}

export enum AccrualItemClosingPeriodFormats {
	YEAR = 'year',
	MONTH_AND_YEAR = 'month and year',
}

export enum MonthCloseStatuses {
	DRAFT = 'draft',
	CLOSED = 'closed',
}

export enum MonthCloseCategories {
	DIRECT_POST = 'direct-post',
	MONTH_CLOSE = 'month-close',
}

export enum ContractCompletionTypes {
	TC_IN_DELIVERY = 'tcInDelivery',
	NEXT_CONTRACT = 'nextContract',
}

export enum ConsumptionRelationTypes {
	MAIN = 'main',
	SECONDARY = 'secondary',
}

export enum SalinityTypes {
	SALT = 'salt',
	FRESH = 'fresh',
	BRACKISH = 'brackish',
}

export const SalinityDensities: Record<SalinityTypes, number> = {
	[SalinityTypes.SALT]: 1025,
	[SalinityTypes.BRACKISH]: 1010,
	[SalinityTypes.FRESH]: 1000,
};

export const MIN_DENSITY = 900;
export const MAX_DENSITY = 1030;

export enum DraftUnits {
	METERS = 'm',
	FEET = 'ft',
}

export const FEET_PER_METER = 3.28084;

export enum BunkerStemAdditionalCostTypes {
	LUMPSUM = 'lumpsum',
	PERCENTAGE = 'percentage',
}

export enum EUAVerifications {
	ESTIMATED = 'estimated',
	VERIFIED = 'verified',
}

export enum VoyagePaymentTypes {
	INVOICE = 'invoice',
	EUA = 'eua',
}

// See https://emailregex.com/
export const EmailRegex = '(?:[a-z0-9!#$%&\'*+\\/=?^_`{|}~-]+(?:\\.[a-z0-9!#$%&\'*+\\/=?' +
	'^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\\\[\x01-\x09\x0b' +
	'\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9' +
	'])?|\\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9]' +
	'[0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\\\[\x01-\x09' +
	'\x0b\x0c\x0e-\x7f])+)\\])';

export enum DateFormats {
	DMY = 'DD-MM-YYYY HH:mm',
	YMD = 'YYYY-MM-DD HH:mm',
	MDY = 'MM-DD-YYYY HH:mm',
}

export enum GlobalLaytimeCalculationMethod {
	TIMETOCOUNT = 'time to count',
	DEDUCTIONS = 'deductions',
}

export enum AccountingItemParentTypes {
	HIRE_INVOICE = 'hireInvoice',
	VOYAGE_EXPENSE = 'expense',
	DISBURSEMENT_ACCOUNT = 'disbursementAccount',
	BROKER_INVOICE = 'brokerInvoice',
	BUNKER_CONSUMPTION = 'bunkerConsumption',
	BUNKER_ADJUSTMENT = 'bunkerAdjustment',
	BUNKER_SALE = 'bunkerSale',
	BUNKER_LIFTING = 'bunkerLifting',
}

export enum ExpenseStatus {
	PENDING = 'Pending',
	INVOICED = 'Invoiced',
	PARTIAL = 'Partial',
	OVERPAID = 'Overpaid',
	PAID = 'Paid',
	OVERDUE = 'Overdue',
}

export enum PayableTypes {
	CHARTERERS_EXPENSE = 'Charterer\'s Expense',
	OWNERS_EXPENSE = 'Owners Expense',
	BUNKERS_ON_TC = 'Bunkers on TC',
	OFF_HIRE = 'Off Hire',
}

export enum ParcelAccountTypes {
	OWNER = 'owner',
	TC_IN = 'tcIn',
}

export enum PortDAStatusTypes {
	PDA = 'PDA',
	FDA = 'FDA'
}

export const TsReportTypeKeys = Object.entries(TsReportTypes).reduce((acc, [key, label]) => {
	acc[label] = Number(key);

	return acc;
}, {} as Record<string, number>);

export enum PnlBunkerTypes {
	PARCEL = 'parcel',
	DELIVERY = 'delivery',
}

// Used to check if the returned message from the API fits, as we need to insert a dynamic link 
// for redirection
export enum DeliveryRedeliveryNotifications {
	DELIVERY = 'Please set the price of bunkers on delivery',
	REDELIVERY = 'Please set the price of bunkers on redelivery',
	MISMATCH_DELIVERY = 'Min / max values of itinerary bunkers and BoD does not match correctly',
	MISMATCH_REDELIVERY = 'Min / max values of itinerary bunkers and BoR does not match correctly',
}

export enum FuelQueueSources {
	DELIVERY = 'delivery',
	ESTIMATED_DELIVERY = 'estimatedDelivery',
	REDELIVERY = 'redelivery',
	ESTIMATED_REDELIVERY = 'estimatedRedelivery',
	BUNKER_STEM = 'bunkerStem',
	MANUAL = 'manual',
}

// FuelEU
export const GHG_INTENSITY_REFERENCE = 91.16;
export const GWP_CO2 = 1.00;
export const GWP_CH4 = 28.00;
export const GWP_N2O = 265.00;

export const CO2EQ_TTW_SLIP = 28;
export const CSF_CO2_SLIP = 0;
export const CSF_CH4_SLIP = 1;
export const CSF_N2O_SLIP = 0;
export const C_SLIP_MULTIPLIED_CO2EQ_TTW_SLIP = 0;

export const ENERGY_PER_TONNE_OF_FUEL = 2400;
export const EMISSION_NORMALIZATION_FACTOR = 41000;

export enum FuelClass {
	FOSSIL = 'Fossil',
	BIOFUELS = 'Biofuels',
	RFNBO = 'RFNBO',
}

export type FuelData = {
	pathwayName: Values<typeof FuelTypes>;
	fuelClass: string;
	lcv: number;
	e: string;
	co2eqWtt: number;
	fuelConsumerUnitClass: string | null;
	cfCo2: number;
	cfCh4: number;
	cfN2o: number;
	cslip: number;
	co2eqWttInclRwd: number;
	co2eqTtwInclSlip: number;
	co2eqWtw: number;
	complianceYear: number | string;
	note: string | null;
};

export const FuelEuData =
	[
		{
			pathwayName: FuelTypes.HSFO,
			fuelClass: FuelClass.FOSSIL,
			lcv: 0.0405,
			e: '-',
			co2eqWtt: 13.5,
			fuelConsumerUnitClass: 'ALL ICEs',
			cfCo2: 3.114,
			cfCh4: 5e-05,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: 13.5,
			co2eqTtwInclSlip: 78.10123457,
			co2eqWtw: 91.60123457,
			complianceYear: 2025,
			note: '',
		},
		{
			pathwayName: 'VLSFO',
			fuelClass: FuelClass.FOSSIL,
			lcv: 0.041,
			e: '-',
			co2eqWtt: 13.2,
			fuelConsumerUnitClass: 'ALL ICEs',
			cfCo2: 3.151,
			cfCh4: 5e-05,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: 13.2,
			co2eqTtwInclSlip: 78.05121951,
			co2eqWtw: 91.25121951,
			complianceYear: 2025,
			note: '',
		},
		{
			pathwayName: 'ULSFO',
			fuelClass: FuelClass.FOSSIL,
			lcv: 0.041,
			e: '-',
			co2eqWtt: 13.2,
			fuelConsumerUnitClass: 'ALL ICEs',
			cfCo2: 3.151,
			cfCh4: 5e-05,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: 13.2,
			co2eqTtwInclSlip: 78.05121951,
			co2eqWtw: 91.25121951,
			complianceYear: 2025,
			note: '',
		},
		{
			pathwayName: FuelTypes.LSFO,
			fuelClass: FuelClass.FOSSIL,
			lcv: 0.041,
			e: '-',
			co2eqWtt: 13.2,
			fuelConsumerUnitClass: 'ALL ICEs',
			cfCo2: 3.151,
			cfCh4: 5e-05,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: 13.2,
			co2eqTtwInclSlip: 78.05121951,
			co2eqWtw: 91.25121951,
			complianceYear: 2025,
			note: '',
		},
		{
			pathwayName: FuelTypes.ULSDO,
			fuelClass: FuelClass.FOSSIL,
			lcv: 0.0427,
			e: '-',
			co2eqWtt: 14.4,
			fuelConsumerUnitClass: 'ALL ICEs',
			cfCo2: 3.206,
			cfCh4: 5e-05,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: 14.4,
			co2eqTtwInclSlip: 76.23185012,
			co2eqWtw: 90.63185012,
			complianceYear: 2025,
			note: '',
		},
		{
			pathwayName: FuelTypes.VLSDO,
			fuelClass: FuelClass.FOSSIL,
			lcv: 0.0427,
			e: '-',
			co2eqWtt: 14.4,
			fuelConsumerUnitClass: 'ALL ICEs',
			cfCo2: 3.206,
			cfCh4: 5e-05,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: 14.4,
			co2eqTtwInclSlip: 76.23185012,
			co2eqWtw: 90.63185012,
			complianceYear: 2025,
			note: '',
		},
		{
			pathwayName: FuelTypes.LSDO,
			fuelClass: FuelClass.FOSSIL,
			lcv: 0.0427,
			e: '-',
			co2eqWtt: 14.4,
			fuelConsumerUnitClass: 'ALL ICEs',
			cfCo2: 3.206,
			cfCh4: 5e-05,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: 14.4,
			co2eqTtwInclSlip: 76.23185012,
			co2eqWtw: 90.63185012,
			complianceYear: 2025,
			note: '',
		},
		{
			pathwayName: FuelTypes.LSMGO,
			fuelClass: FuelClass.FOSSIL,
			lcv: 0.0427,
			e: '-',
			co2eqWtt: 14.4,
			fuelConsumerUnitClass: 'ALL ICEs',
			cfCo2: 3.206,
			cfCh4: 5e-05,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: 14.4,
			co2eqTtwInclSlip: 76.23185012,
			co2eqWtw: 90.63185012,
			complianceYear: 2025,
			note: '',
		},
		{
			pathwayName: FuelTypes.HSDO,
			fuelClass: FuelClass.FOSSIL,
			lcv: 0.0427,
			e: '-',
			co2eqWtt: 14.4,
			fuelConsumerUnitClass: 'ALL ICEs',
			cfCo2: 3.206,
			cfCh4: 5e-05,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: 14.4,
			co2eqTtwInclSlip: 76.23185012,
			co2eqWtw: 90.63185012,
			complianceYear: 2025,
			note: '',
		},
		{
			pathwayName: 'MGO',
			fuelClass: FuelClass.FOSSIL,
			lcv: 0.0427,
			e: '-',
			co2eqWtt: 14.4,
			fuelConsumerUnitClass: 'ALL ICEs',
			cfCo2: 3.206,
			cfCh4: 5e-05,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: 14.4,
			co2eqTtwInclSlip: 76.23185012,
			co2eqWtw: 90.63185012,
			complianceYear: 2025,
			note: '',
		},
		{
			pathwayName: 'LNG Otto - Medium Speed',
			fuelClass: FuelClass.FOSSIL,
			lcv: 0.0491,
			e: '-',
			co2eqWtt: 18.5,
			fuelConsumerUnitClass: 'LNG Otto (dual fuel medium speed)',
			cfCo2: 2.75,
			cfCh4: 0.0,
			cfN2o: 0.00011,
			cslip: 3.1,
			co2eqWttInclRwd: 18.5,
			co2eqTtwInclSlip: 72.52538391,
			co2eqWtw: 91.02538391,
			complianceYear: 2025,
			note: '',
		},
		{
			pathwayName: 'LNG Otto - Slow Speed',
			fuelClass: FuelClass.FOSSIL,
			lcv: 0.0491,
			e: '-',
			co2eqWtt: 18.5,
			fuelConsumerUnitClass: 'LNG Otto (dual fuel slow speed)',
			cfCo2: 2.75,
			cfCh4: 0.0,
			cfN2o: 0.00011,
			cslip: 1.7,
			co2eqWttInclRwd: 18.5,
			co2eqTtwInclSlip: 65.33410285,
			co2eqWtw: 83.83410285,
			complianceYear: 2035,
			note: '',
		},
		{
			pathwayName: 'LNG Diesel - Slow Speed',
			fuelClass: FuelClass.FOSSIL,
			lcv: 0.0491,
			e: '-',
			co2eqWtt: 18.5,
			fuelConsumerUnitClass: 'LNG Diesel (dual fuel slow speed)',
			cfCo2: 2.75,
			cfCh4: 0.0,
			cfN2o: 0.00011,
			cslip: 0.2,
			co2eqWttInclRwd: 18.5,
			co2eqTtwInclSlip: 57.62915886,
			co2eqWtw: 76.12915886,
			complianceYear: 2040,
			note: '',
		},
		{
			pathwayName: FuelTypes.LNG,
			fuelClass: FuelClass.FOSSIL,
			lcv: 0.0491,
			e: '-',
			co2eqWtt: 18.5,
			fuelConsumerUnitClass: 'LNG Diesel (dual fuel slow speed)',
			cfCo2: 2.75,
			cfCh4: 0.0,
			cfN2o: 0.00011,
			cslip: 0.2,
			co2eqWttInclRwd: 18.5,
			co2eqTtwInclSlip: 57.62915886,
			co2eqWtw: 76.12915886,
			complianceYear: 2040,
			note: '',
		},
		{
			pathwayName: 'LNG - LBSI',
			fuelClass: FuelClass.FOSSIL,
			lcv: 0.0491,
			e: '-',
			co2eqWtt: 18.5,
			fuelConsumerUnitClass: 'LBSI',
			cfCo2: 2.75,
			cfCh4: 0.0,
			cfN2o: 0.00011,
			cslip: 2.6,
			co2eqWttInclRwd: 18.5,
			co2eqTtwInclSlip: 69.95706925,
			co2eqWtw: 88.45706925,
			complianceYear: 2030,
			note: '',
		},
		{
			pathwayName: 'LPG',
			fuelClass: FuelClass.FOSSIL,
			lcv: 0.046,
			e: '-',
			co2eqWtt: 7.8,
			fuelConsumerUnitClass: 'ALL ICEs',
			cfCo2: 3.0,
			cfCh4: 5e-05,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: 7.8,
			co2eqTtwInclSlip: 66.28478261,
			co2eqWtw: 74.08478261,
			complianceYear: 2040,
			note: '',
		},
		{
			pathwayName: 'H2',
			fuelClass: FuelClass.FOSSIL,
			lcv: 0.12,
			e: '-',
			co2eqWtt: 132.0,
			fuelConsumerUnitClass: 'Fuel cells & ICEs',
			cfCo2: 0.0,
			cfCh4: 0.0,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: 132.0,
			co2eqTtwInclSlip: 0.3975,
			co2eqWtw: 132.3975,
			complianceYear: 2025,
			note: '',
		},
		{
			pathwayName: 'NH3',
			fuelClass: FuelClass.FOSSIL,
			lcv: 0.0186,
			e: '-',
			co2eqWtt: 121.0,
			fuelConsumerUnitClass: 'Fuel cells & ICEs',
			cfCo2: 0.0,
			cfCh4: 0.0,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: 121.0,
			co2eqTtwInclSlip: 2.564516129,
			co2eqWtw: 123.5645161,
			complianceYear: 2025,
			note: '',
		},
		{
			pathwayName: 'Methanol',
			fuelClass: FuelClass.FOSSIL,
			lcv: 0.0199,
			e: '-',
			co2eqWtt: 31.3,
			fuelConsumerUnitClass: 'ALL ICEs',
			cfCo2: 1.375,
			cfCh4: 0.0,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: 31.3,
			co2eqTtwInclSlip: 71.49246231,
			co2eqWtw: 102.7924623,
			complianceYear: 2025,
			note: '',
		},
		{
			pathwayName: 'Ethanol',
			fuelClass: FuelClass.BIOFUELS,
			lcv: 0.027,
			e: 30.7,
			co2eqWtt: -40.15185185,
			fuelConsumerUnitClass: 'ALL ICEs',
			cfCo2: 1.913,
			cfCh4: 5e-05,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: -40.15185185,
			co2eqTtwInclSlip: 72.67037037,
			co2eqWtw: 32.51851852,
			complianceYear: 2050,
			note: '',
		},
		{
			pathwayName: 'Bio-diesel',
			fuelClass: FuelClass.BIOFUELS,
			lcv: 0.037,
			e: 27.25,
			co2eqWtt: -49.34459459,
			fuelConsumerUnitClass: 'ALL ICEs',
			cfCo2: 2.834,
			cfCh4: 5e-05,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: -49.34459459,
			co2eqTtwInclSlip: 77.92162162,
			co2eqWtw: 28.57702703,
			complianceYear: 2050,
			note: '',
		},
		{
			pathwayName: 'HVO',
			fuelClass: FuelClass.BIOFUELS,
			lcv: 0.044,
			e: 37.37,
			co2eqWtt: -33.42545455,
			fuelConsumerUnitClass: 'ALL ICEs',
			cfCo2: 3.115,
			cfCh4: 5e-05,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: -33.42545455,
			co2eqTtwInclSlip: 71.91136364,
			co2eqWtw: 38.48590909,
			complianceYear: 2045,
			note: '',
		},
		{
			pathwayName: 'Bio-LNG Otto - Medium Speed',
			fuelClass: FuelClass.BIOFUELS,
			lcv: 0.05,
			e: 29.98,
			co2eqWtt: -25.02,
			fuelConsumerUnitClass: 'LNG Otto (dual fuel medium speed)',
			cfCo2: 2.75,
			cfCh4: 0.0,
			cfN2o: 0.00011,
			cslip: 3.1,
			co2eqWttInclRwd: -25.02,
			co2eqTtwInclSlip: 71.219927,
			co2eqWtw: 46.199927,
			complianceYear: 2045,
			note: '',
		},
		{
			pathwayName: 'Bio-LNG Otto - Slow Speed',
			fuelClass: FuelClass.BIOFUELS,
			lcv: 0.05,
			e: 29.98,
			co2eqWtt: -25.02,
			fuelConsumerUnitClass: 'LNG Otto (dual fuel slow speed)',
			cfCo2: 2.75,
			cfCh4: 0.0,
			cfN2o: 0.00011,
			cslip: 1.7,
			co2eqWttInclRwd: -25.02,
			co2eqTtwInclSlip: 64.158089,
			co2eqWtw: 39.138089,
			complianceYear: 2045,
			note: '',
		},
		{
			pathwayName: 'Bio-LNG Diesel - Slow Speed',
			fuelClass: FuelClass.BIOFUELS,
			lcv: 0.05,
			e: 29.98,
			co2eqWtt: -25.02,
			fuelConsumerUnitClass: 'LNG Diesel (dual fuel slow speed)',
			cfCo2: 2.75,
			cfCh4: 0.0,
			cfN2o: 0.00011,
			cslip: 0.2,
			co2eqWttInclRwd: -25.02,
			co2eqTtwInclSlip: 56.591834,
			co2eqWtw: 31.571834,
			complianceYear: 2050,
			note: '',
		},
		{
			pathwayName: 'Bio-LNG - LBSI',
			fuelClass: FuelClass.BIOFUELS,
			lcv: 0.05,
			e: 29.98,
			co2eqWtt: -25.02,
			fuelConsumerUnitClass: 'LBSI',
			cfCo2: 2.75,
			cfCh4: 0.0,
			cfN2o: 0.00011,
			cslip: 2.6,
			co2eqWttInclRwd: -25.02,
			co2eqTtwInclSlip: 68.697842,
			co2eqWtw: 43.677842,
			complianceYear: 2045,
			note: '',
		},
		{
			pathwayName: 'Bio-methanol',
			fuelClass: FuelClass.BIOFUELS,
			lcv: 0.02,
			e: 13.37,
			co2eqWtt: -55.38,
			fuelConsumerUnitClass: 'ALL ICEs',
			cfCo2: 1.375,
			cfCh4: 0.0,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: -55.38,
			co2eqTtwInclSlip: 71.135,
			co2eqWtw: 15.755,
			complianceYear: '2050+',
			note: '',
		},
		{
			pathwayName: 'Other',
			fuelClass: FuelClass.BIOFUELS,
			lcv: 0.041,
			e: 24.85,
			co2eqWtt: -51.12560976,
			fuelConsumerUnitClass: 'ALL ICEs',
			cfCo2: 3.115,
			cfCh4: 5e-05,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: -51.12560976,
			co2eqTtwInclSlip: 77.17317073,
			co2eqWtw: 26.04756098,
			complianceYear: 2050,
			note: '',
		},
		{
			pathwayName: 'Bio-H2',
			fuelClass: FuelClass.BIOFUELS,
			lcv: 0.12,
			e: '-',
			co2eqWtt: 29.98,
			fuelConsumerUnitClass: 'Fuel cells & ICEs',
			cfCo2: 0.0,
			cfCh4: 0.0,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: 29.98,
			co2eqTtwInclSlip: 0.3975,
			co2eqWtw: 30.3775,
			complianceYear: 2050,
			note: '',
		},
		{
			pathwayName: 'e-diesel',
			fuelClass: FuelClass.RFNBO,
			lcv: 0.0427,
			e: '-',
			co2eqWtt: 27.25,
			fuelConsumerUnitClass: 'ALL ICEs',
			cfCo2: 3.206,
			cfCh4: 5e-05,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: 13.625,
			co2eqTtwInclSlip: 38.11592506,
			co2eqWtw: 51.74092506,
			complianceYear: 2045,
			note: '',
		},
		{
			pathwayName: 'e-methanol',
			fuelClass: FuelClass.RFNBO,
			lcv: 0.0199,
			e: '-',
			co2eqWtt: 13.37,
			fuelConsumerUnitClass: 'ALL ICEs',
			cfCo2: 1.375,
			cfCh4: 5e-05,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: 6.685,
			co2eqTtwInclSlip: 35.78140704,
			co2eqWtw: 42.46640704,
			complianceYear: 2045,
			note: '',
		},
		{
			pathwayName: 'e-LNG Otto - Medium Speed',
			fuelClass: FuelClass.RFNBO,
			lcv: 0.0491,
			e: '-',
			co2eqWtt: 29.98,
			fuelConsumerUnitClass: 'LNG Otto (dual fuel medium speed)',
			cfCo2: 2.75,
			cfCh4: 0.0,
			cfN2o: 0.00011,
			cslip: 3.1,
			co2eqWttInclRwd: 14.99,
			co2eqTtwInclSlip: 36.26269196,
			co2eqWtw: 51.25269196,
			complianceYear: 2045,
			note: '',
		},
		{
			pathwayName: 'e-LNG Otto - Slow Speed',
			fuelClass: FuelClass.RFNBO,
			lcv: 0.0491,
			e: '-',
			co2eqWtt: 29.98,
			fuelConsumerUnitClass: 'LNG Otto (dual fuel slow speed)',
			cfCo2: 2.75,
			cfCh4: 0.0,
			cfN2o: 0.00011,
			cslip: 1.7,
			co2eqWttInclRwd: 14.99,
			co2eqTtwInclSlip: 32.66705143,
			co2eqWtw: 47.65705143,
			complianceYear: 2045,
			note: '',
		},
		{
			pathwayName: 'e-LNG Diesel - Slow Speed',
			fuelClass: FuelClass.RFNBO,
			lcv: 0.0491,
			e: '-',
			co2eqWtt: 29.98,
			fuelConsumerUnitClass: 'LNG Diesel (dual fuel slow speed)',
			cfCo2: 2.75,
			cfCh4: 0.0,
			cfN2o: 0.00011,
			cslip: 0.2,
			co2eqWttInclRwd: 14.99,
			co2eqTtwInclSlip: 28.81457943,
			co2eqWtw: 43.80457943,
			complianceYear: 2045,
			note: '',
		},
		{
			pathwayName: 'e-LNG - LBSI',
			fuelClass: FuelClass.RFNBO,
			lcv: 0.0491,
			e: '-',
			co2eqWtt: 29.98,
			fuelConsumerUnitClass: 'LBSI',
			cfCo2: 2.75,
			cfCh4: 0.0,
			cfN2o: 0.00011,
			cslip: 2.6,
			co2eqWttInclRwd: 14.99,
			co2eqTtwInclSlip: 34.97853462,
			co2eqWtw: 49.96853462,
			complianceYear: 2045,
			note: '',
		},
		{
			pathwayName: 'e-H2',
			fuelClass: FuelClass.RFNBO,
			lcv: 0.12,
			e: '-',
			co2eqWtt: 29.98,
			fuelConsumerUnitClass: 'Fuel cells & ICEs',
			cfCo2: 0.0,
			cfCh4: 0.0,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: 14.99,
			co2eqTtwInclSlip: 0.19875,
			co2eqWtw: 15.18875,
			complianceYear: '2050+',
			note: '',
		},
		{
			pathwayName: 'e-NH3',
			fuelClass: FuelClass.RFNBO,
			lcv: 0.0186,
			e: '-',
			co2eqWtt: 29.98,
			fuelConsumerUnitClass: 'Fuel cells & ICEs',
			cfCo2: 0.0,
			cfCh4: 0.0,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: 14.99,
			co2eqTtwInclSlip: 1.282258065,
			co2eqWtw: 16.27225806,
			complianceYear: '2050+',
			note: '',
		},
		{
			pathwayName: 'e-LPG',
			fuelClass: FuelClass.RFNBO,
			lcv: 0.046,
			e: '-',
			co2eqWtt: 29.98,
			fuelConsumerUnitClass: '',
			cfCo2: 3.0,
			cfCh4: 5e-05,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: 14.99,
			co2eqTtwInclSlip: 33.1423913,
			co2eqWtw: 48.1323913,
			complianceYear: 2045,
			note: '',
		},
		{
			pathwayName: 'e-DME',
			fuelClass: FuelClass.RFNBO,
			lcv: 0.0288,
			e: '-',
			co2eqWtt: 29.98,
			fuelConsumerUnitClass: '',
			cfCo2: 1.91,
			cfCh4: 5e-05,
			cfN2o: 0.00018,
			cslip: 0.0,
			co2eqWttInclRwd: 14.99,
			co2eqTtwInclSlip: 34.01215278,
			co2eqWtw: 49.00215278,
			complianceYear: 2040,
			note: '',
		},
	];

export const FuelEuPenlaties =
	[
		{
			'year': 2025,
			'reductionFactor': 0.02,
			'ghgIntensityTarget': 89.337,
			'penaltyMultiplier': 1.0,
			'complianceBalance': -183419.2,
			'consecutiveUnderperformance': true,
		},
		{
			'year': 2026,
			'reductionFactor': 0.02,
			'ghgIntensityTarget': 89.337,
			'penaltyMultiplier': 1.1,
			'complianceBalance': -183419.2,
			'consecutiveUnderperformance': true,
		},
		{
			'year': 2027,
			'reductionFactor': 0.02,
			'ghgIntensityTarget': 89.337,
			'penaltyMultiplier': 1.2,
			'complianceBalance': -183419.2,
			'consecutiveUnderperformance': true,
		},
		{
			'year': 2028,
			'reductionFactor': 0.02,
			'ghgIntensityTarget': 89.337,
			'penaltyMultiplier': 1.3,
			'complianceBalance': -183419.2,
			'consecutiveUnderperformance': true,
		},
		{
			'year': 2029,
			'reductionFactor': 0.02,
			'ghgIntensityTarget': 89.337,
			'penaltyMultiplier': 1.4,
			'complianceBalance': -183419.2,
			'consecutiveUnderperformance': true,
		},
		{
			'year': 2030,
			'reductionFactor': 0.06,
			'ghgIntensityTarget': 85.69,
			'penaltyMultiplier': 1.5,
			'complianceBalance': -478777.6,
			'consecutiveUnderperformance': true,
		},
		{
			'year': 2031,
			'reductionFactor': 0.06,
			'ghgIntensityTarget': 85.69,
			'penaltyMultiplier': 1.6,
			'complianceBalance': -478777.6,
			'consecutiveUnderperformance': true,
		},
		{
			'year': 2032,
			'reductionFactor': 0.06,
			'ghgIntensityTarget': 85.69,
			'penaltyMultiplier': 1.7,
			'complianceBalance': -478777.6,
			'consecutiveUnderperformance': true,
		},
		{
			'year': 2033,
			'reductionFactor': 0.06,
			'ghgIntensityTarget': 85.69,
			'penaltyMultiplier': 1.8,
			'complianceBalance': -478777.6,
			'consecutiveUnderperformance': true,
		},
		{
			'year': 2034,
			'reductionFactor': 0.06,
			'ghgIntensityTarget': 85.69,
			'penaltyMultiplier': 1.9,
			'complianceBalance': -478777.6,
			'consecutiveUnderperformance': true,
		},
		{
			'year': 2035,
			'reductionFactor': 0.145,
			'ghgIntensityTarget': 77.942,
			'penaltyMultiplier': 2.0,
			'complianceBalance': -1106414.2,
			'consecutiveUnderperformance': true,
		},
		{
			'year': 2036,
			'reductionFactor': 0.145,
			'ghgIntensityTarget': 77.942,
			'penaltyMultiplier': 2.1,
			'complianceBalance': -1106414.2,
			'consecutiveUnderperformance': true,
		},
		{
			'year': 2037,
			'reductionFactor': 0.145,
			'ghgIntensityTarget': 77.942,
			'penaltyMultiplier': 2.2,
			'complianceBalance': -1106414.2,
			'consecutiveUnderperformance': true,
		},
		{
			'year': 2038,
			'reductionFactor': 0.145,
			'ghgIntensityTarget': 77.942,
			'penaltyMultiplier': 2.3,
			'complianceBalance': -1106414.2,
			'consecutiveUnderperformance': true,
		},
		{
			'year': 2039,
			'reductionFactor': 0.145,
			'ghgIntensityTarget': 77.942,
			'penaltyMultiplier': 2.4,
			'complianceBalance': -1106414.2,
			'consecutiveUnderperformance': true,
		},
		{
			'year': 2040,
			'reductionFactor': 0.31,
			'ghgIntensityTarget': 62.9,
			'penaltyMultiplier': 2.5,
			'complianceBalance': -2324767.6,
			'consecutiveUnderperformance': true,
		},
		{
			'year': 2041,
			'reductionFactor': 0.31,
			'ghgIntensityTarget': 62.9,
			'penaltyMultiplier': 2.6,
			'complianceBalance': -2324767.6,
			'consecutiveUnderperformance': true,
		},
		{
			'year': 2042,
			'reductionFactor': 0.31,
			'ghgIntensityTarget': 62.9,
			'penaltyMultiplier': 2.7,
			'complianceBalance': -2324767.6,
			'consecutiveUnderperformance': true,
		},
		{
			'year': 2043,
			'reductionFactor': 0.31,
			'ghgIntensityTarget': 62.9,
			'penaltyMultiplier': 2.8,
			'complianceBalance': -2324767.6,
			'consecutiveUnderperformance': true,
		},
		{
			'year': 2044,
			'reductionFactor': 0.31,
			'ghgIntensityTarget': 62.9,
			'penaltyMultiplier': 2.9,
			'complianceBalance': -2324767.6,
			'consecutiveUnderperformance': true,
		},
		{
			'year': 2045,
			'reductionFactor': 0.62,
			'ghgIntensityTarget': 34.641,
			'penaltyMultiplier': 3.0,
			'complianceBalance': -4613795.2,
			'consecutiveUnderperformance': true,
		},
		{
			'year': 2046,
			'reductionFactor': 0.62,
			'ghgIntensityTarget': 34.641,
			'penaltyMultiplier': 3.1,
			'complianceBalance': -4613795.2,
			'consecutiveUnderperformance': true,
		},
		{
			'year': 2047,
			'reductionFactor': 0.62,
			'ghgIntensityTarget': 34.641,
			'penaltyMultiplier': 3.2,
			'complianceBalance': -4613795.2,
			'consecutiveUnderperformance': true,
		},
		{
			'year': 2048,
			'reductionFactor': 0.62,
			'ghgIntensityTarget': 34.641,
			'penaltyMultiplier': 3.3,
			'complianceBalance': -4613795.2,
			'consecutiveUnderperformance': true,
		},
		{
			'year': 2049,
			'reductionFactor': 0.62,
			'ghgIntensityTarget': 34.641,
			'penaltyMultiplier': 3.4,
			'complianceBalance': -4613795.2,
			'consecutiveUnderperformance': true,
		},
		{
			'year': 2050,
			'reductionFactor': 0.8,
			'ghgIntensityTarget': 18.232,
			'penaltyMultiplier': 3.5,
			'complianceBalance': -5942908.0,
			'consecutiveUnderperformance': true,
		},
	];

export enum CsvDelimiter {
	Comma = ',',
	Semicolon = ';',
}

export enum NumberFormatStyle {
	UkUs = 'uk-us',
	European = 'european',
}

export enum CargoStatus {
	Active = 'active',
	Complete = 'complete',
}

export enum CargoContractSides {
	VC_OUT = 'vcOut',
	VC_IN = 'vcIn',
}

export enum ConsumptionSetAttachedTo {
	VESSEL = 'VESSEL',
	VOYAGE = 'VOYAGE',
	ESTIMATE = 'ESTIMATE',
	CARGO_RELET = 'CARGO RELET',
}
export enum RequiredArrivalSpeedStatus {
	MATCHED = 'matched',
	TOO_FAST = 'too-fast',
	TOO_SLOW = 'too-slow',
	SINGLE_SPEED_FOR_FUEL_GRADE = 'single-speed-for-fuel-grade',
}
